import { useCompacto } from '../../shell/compacto.ts'
import { usePermission } from '../../lib/access-control.ts'
import { Alert, LoadingState, Modal, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useMemo, useRef, useState, type PointerEvent as EventoDePonteiro, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { api } from '../../lib/api.ts'
import { useBootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { useSession } from '../../lib/session.tsx'
import { usePreferenciaDeLayout, usePreferenciaEstruturada } from '../../lib/layout-prefs.ts'
import { LinhaDoTempo, type LinhaDoTempoLinha } from './LinhaDoTempo.tsx'
import { iniciarArrasteDeRolagem } from './arrastar-para-rolar.ts'
import { ARVORE_PADRAO, adotarArvore, alternarNaArvore, recolhidosDe, tudoNaArvore, type ArvoreGuardada } from './arvore-recolhida.ts'
import { useColunasParaTodos, useWorkItemDetail, useWorkItemList, type QuickFilterCriteria } from './api.ts'
import { useMutacoesDeVisao, useVisoes } from '../visao/api.ts'
import type { ConfigDaVisao } from '@orbind/core'
import { aplicarColunasDoFiltro, colunasLigadas, receberParaTodos } from './colunas-do-filtro.ts'
import { iniciarArrasteDeColuna } from '../../lib/arrastar-coluna.ts'
import { StatusPill, initialsOf } from './bits.tsx'
import { WorkItemDetailView } from './WorkItemDetail.tsx'
import { ResumoDoItem } from './ResumoDoItem.tsx'
import { FiltroDaLista, selecaoDaVisao, type WiSelecao } from './QuickFilterBar.tsx'
import { WorkItemChildrenView } from './WorkItemChildrenView.tsx'
import type { FilterField } from '@orbind/ui'
import { CAMPOS_DE_FILTRO, fazerLoadValueOptions, fazerRowValues, tipoDoCampoComplementar } from './filter-fields.ts'
import { MotivoDaReabertura } from './MotivoDaReabertura.tsx'
import { ConcluirEmCadeiaModal, type NoDaCascata } from './ConcluirEmCadeiaModal.tsx'
import { ReabrirEmCadeiaModal } from './ReabrirEmCadeiaModal.tsx'
import { proximasEtapas, rotuloDaEtapa } from './proximas-etapas.ts'
import { useCustomFields } from '../admin/custom-field-data.ts'
import { useCatalogoDeColunas } from './catalogo-de-colunas.ts'
import { rotuloCampo } from './campos-shared.tsx'
import {
  PREFIXO_COMPLEMENTAR,
  PREFIXO_ETAPA,
  PREFIXO_STATUS,
  adotarEscolhaAntiga,
  adotarTabela,
  definicaoDaColuna,
  fazerComparador,
  moverColuna as moverNaLista,
  moverParaOLugarDe,
  proximaOrdenacao,
  catalogoDeColunas,
  padraoDaTabela,
  rotuloDaColuna,
  trilhosDaGrade,
  type ColunaKey,
  type EstadoColuna,
  type EstadoTabela,
} from './colunas-da-tabela.ts'
import {
  criarProgressoDosItens,
  evaluateFilter,
  linhaDeChecklistFeita,
  type LinhaDoProgresso,
  type SavedFilter,
} from '@orbind/core'
import { WorkspaceBar } from '../workspace/WorkspaceBar.tsx'
import { useTemplates } from '../admin/template-data.ts'
import { EscolherTemplateModal } from './TemplatesUI.tsx'
import './wi.css'

/**
 * Onde a escolha de colunas morava antes de ir para a conta
 * (SPEC-20261003-0002). Lida uma vez, para subir o que a pessoa já tinha, e
 * apagada em seguida.
 */
const CHAVE_LEGADA_COLUNAS = 'ob-wi-colunas'

/**
 * Itens de Trabalho — split view do prototipo v2: explorer a esquerda
 * (lista plana ou arvore) e o detalhe `showWi` a direita.
 */
/** Desenha `children` em `alvo` quando há um; senão, ali mesmo. */
function NoSlot({ alvo, children }: { alvo?: HTMLElement | null; children: ReactNode }) {
  return alvo ? createPortal(children, alvo) : <>{children}</>
}

/**
 * A tela de Itens de Trabalho é montada em DOIS lugares (SPEC-20261003-1059):
 * nela mesma e dentro do quadro, no modo Lista. É a mesma tela, e não uma cópia
 * — duas tabelas divergiriam na primeira coluna nova.
 *
 * `kanbanBoardId` recorta a lista pelos itens daquele quadro. `embutido` diz
 * que ela está DENTRO de outra tela: aí não desenha a barra de workspace (a de
 * fora já desenhou) e nunca abre a edição no lugar da lista — clicar no código
 * leva para a página do item, saindo do quadro.
 */
/** A visão em uso na lista (SPEC-20261103-1418), vinda do combo ou do quadro. */
export type VisaoNaLista = {
  id: string
  colunas?: readonly string[]
  ordenacao?: ConfigDaVisao['ordenacao']
  modo?: ConfigDaVisao['modo']
  /** null = a pessoa só usa: mexer vale na tela, sem gravar. */
  alterar: ((config: Partial<ConfigDaVisao>) => void) | null
}

export function WorkItemsPage({
  kanbanBoardId,
  embutido = false,
  controlesDoQuadro,
  slotDoCabecalho,
  modo,
}: {
  kanbanBoardId?: string
  embutido?: boolean
  /**
   * Modo FIXO, decidido por quem embute (SPEC-20261009-2112-linha-do-tempo-no-quadro): o quadro tem
   * "Lista" e "Linha do tempo" como visões próprias, então a tela não mostra o
   * alternador Tabela | Linha do tempo nem usa a escolha guardada no perfil.
   */
  modo?: 'tabela' | 'tempo'
  /**
   * Busca e filtros do CABEÇALHO do quadro
   * (SPEC-20261006-1633-filtro-unico-do-quadro). Presentes, a lista esconde a
   * busca e o filtro dela e obedece aos do quadro: Quadro, Lista e Planilha
   * são três leituras da MESMA seleção, e cada modo com o seu filtro deixava a
   * pessoa trocando de modo e achando outra coisa.
   */
  controlesDoQuadro?: {
    busca: string
    filtros: readonly SavedFilter[]
    /**
     * Só estes itens — os não mapeados, quando o quadro pede
     * (SPEC-20261006-2326-nao-mapeados-busca-e-filtro). `null`: sem recorte.
     */
    soOsIds?: ReadonlySet<string> | null
    /**
     * A VISÃO do quadro em uso (SPEC-20261103-1418): colunas e ordenação da
     * lista. `alterar` null: a pessoa só usa a visão — mexer vale na tela.
     */
    visao?: VisaoNaLista | null
  }
  /**
   * Onde desenhar o CABEÇALHO da lista, quando ele fica fora do painel
   * (SPEC-20261006-2128-lista-de-itens-em-painel). Sem ele, o cabeçalho fica
   * dentro, como sempre.
   */
  slotDoCabecalho?: HTMLElement | null
} = {}) {
  const navigate = useNavigate()


  /*
   * DOIS destinos, dois estados (SPEC-20261002-1541).
   *
   * `resumoId` é o item que o painel mostra, e vive só na tela: clicar na
   * LINHA não navega, então a lista fica onde está.
   *
   * `selectedId` continua vindo da ROTA e é quem abre a edição em tela
   * cheia — é para onde a CHAVE leva.
   *
   * Antes os dois cliques chamavam a mesma navegação, e por isso faziam a
   * mesma coisa: não havia como espiar um item sem sair da lista.
   */
  const [resumoId, setResumoId] = useState<string | null>(null)

  /**
   * Largura do painel e se ele fica FIXADO (SPEC-20261002-1541).
   *
   * Fixado, clicar fora não recolhe — é para quem percorre a lista item a
   * item e quer o resumo parado ali. Solto, o painel se comporta como
   * sobreposição e sai do caminho ao primeiro clique fora.
   *
   * Os dois ficam no navegador: é preferência de quem usa, e a tela não
   * deve esquecê-la a cada visita.
   */
  const [resumoWidth, setResumoWidth] = useState(() => {
    const salvo = Number(localStorage.getItem('ob-wi-resumo-w'))
    /*
      Largura guardada numa janela maior não vale numa menor: o painel abria
      mais largo que a tela e cobria a lista inteira (SPEC-20261010-1707-resumo-do-item-no-painel-e-responsavel).
      Nesse caso volta ao padrão; o CSS ainda segura o máximo ao redimensionar.
    */
    const cabe = typeof window === 'undefined' || salvo <= window.innerWidth - 480
    return Number.isFinite(salvo) && salvo >= 300 && cabe ? salvo : 420
  })
  const [resumoFixado, setResumoFixado] = useState(
    () => localStorage.getItem('ob-wi-resumo-fix') === '1',
  )
  const resumoRef = useRef<HTMLDivElement>(null)
  // a caixa que rola a tabela: arrastar dentro dela move a lista (SPEC-20261010-1623)
  const tabelaRef = useRef<HTMLDivElement>(null)

  /**
   * Colunas da tabela (SPEC-20261003-0002): QUAIS, em QUE ORDEM e com QUE
   * LARGURA. O catálogo e as regras vivem em `colunas-da-tabela.ts`; aqui fica
   * só a ligação com a tela.
   *
   * O ajuste é da PESSOA e vai para a conta, não para o `localStorage` onde
   * nasceu: a largura ajustada no escritório vale em casa, e dois colegas no
   * mesmo computador não herdam a tabela um do outro.
   */
  /**
   * O catálogo de colunas inclui os campos complementares do workspace
   * (SPEC-20261006-1057-colunas-da-lista): eles são cadastrados por quem usa, e
   * uma tabela só com as colunas de fábrica deixaria de fora justamente o que
   * cada empresa acrescentou.
   */
  const { catalogo, catalogoIncompleto, camposComplementares, nosDeFluxo } = useCatalogoDeColunas()

  /** Nome de cada nó do portfólio, para a coluna "Portfólio". */
  const portfolio = useQuery({
    queryKey: ['portfolio', 'tree'],
    queryFn: async () => (await api.portfolio.tree.$get()).json(),
  })
  const nosDoPortfolio = useMemo(() => {
    const mapa = new Map<string, string>()
    const andar = (nos: readonly { id: string; name: string; children?: unknown }[]) => {
      for (const no of nos) {
        mapa.set(no.id, no.name)
        andar((no.children ?? []) as never)
      }
    }
    andar((portfolio.data?.nodes ?? []) as never)
    return mapa
  }, [portfolio.data])
  const padraoDaVez = useMemo(() => padraoDaTabela(catalogo), [catalogo])

  /**
   * O filtro escolhido FICA (SPEC-20261006-1027-filtro-escolhido-fica), na conta da pessoa: sair da
   * tela e voltar reencontra o mesmo recorte, em qualquer computador.
   *
   * Guarda-se o ID, e nao o filtro inteiro: os criterios de um filtro salvo
   * podem ter mudado desde ontem, e aplicar a copia velha mostraria um recorte
   * que ja nao existe. O filtro e reencontrado pela lista ao carregar; sumiu,
   * volta para "todos".
   */
  const [filtroGuardado, setFiltroGuardado] = usePreferenciaEstruturada<{
    v: 1
    tipo: 'builtin' | 'custom'
    id: string
  }>('wi.filtro', { v: 1, tipo: 'builtin', id: 'all' }, (bruto) => {
    if (typeof bruto !== 'object' || bruto === null) return null
    const v = bruto as { v?: unknown; tipo?: unknown; id?: unknown }
    if (v.v !== 1 || typeof v.id !== 'string') return null
    if (v.tipo !== 'builtin' && v.tipo !== 'custom') return null
    return { v: 1, tipo: v.tipo, id: v.id }
  })
  const [selecao, setSelecaoLocal] = useState<WiSelecao>(
    filtroGuardado.tipo === 'builtin' && (filtroGuardado.id === 'mine' || filtroGuardado.id === 'overdue')
      ? { tipo: 'builtin', id: filtroGuardado.id }
      : { tipo: 'builtin', id: 'all' },
  )
  const setSelecao = (nova: WiSelecao) => {
    setSelecaoLocal(nova)
    setFiltroGuardado({ v: 1, tipo: nova.tipo, id: nova.id })
  }

  /**
   * A visão guardada só pode ser reaplicada quando a lista delas chega
   * (SPEC-20261103-1418: os filtros rápidos viraram visões com o mesmo id).
   */
  const visoes = useVisoes(null)
  const reaplicou = useRef(false)
  useEffect(() => {
    if (reaplicou.current || filtroGuardado.tipo !== 'custom') return
    if (!visoes.data) return
    const achada = visoes.data.find((v) => v.id === filtroGuardado.id)
    reaplicou.current = true
    // sumiu (apagada, ou deixou de ser compartilhada): "todos", sem reclamar
    if (achada) setSelecaoLocal(selecaoDaVisao(achada))
  }, [visoes.data, filtroGuardado])
  /** A visão escolhida, como está agora no servidor (alguém pode tê-la editado). */
  const visaoEscolhida =
    !controlesDoQuadro && selecao.tipo === 'custom' ? (visoes.data?.find((v) => v.id === selecao.id) ?? null) : null
  /** Os critérios da visão escolhida — os de agora, não a cópia de quando foi escolhida. */
  const criteriosEscolhidos: QuickFilterCriteria | null =
    selecao.tipo === 'custom' ? ((visaoEscolhida?.config.filtro as QuickFilterCriteria | undefined) ?? selecao.criteria) : null

  const [tabela, setTabela] = usePreferenciaEstruturada<EstadoTabela>(
    'wi.colunas',
    padraoDaVez,
    (bruto) => adotarTabela(bruto, catalogo, catalogoIncompleto),
  )
  const [menuColunas, setMenuColunas] = useState(false)
  const [buscaColuna, setBuscaColuna] = useState('')

  /**
   * Quem já tinha escolhido colunas antes da mudança sobe o que escolheu, uma
   * vez. Sem isso a mudança pareceria perda de trabalho: a tabela voltaria ao
   * padrão sozinha no primeiro acesso.
   */
  const migrou = useRef(false)
  useEffect(() => {
    if (migrou.current) return
    migrou.current = true
    const legado = localStorage.getItem(CHAVE_LEGADA_COLUNAS)
    if (legado === null) return
    localStorage.removeItem(CHAVE_LEGADA_COLUNAS)
    // já existe escolha na conta: ela vence, e o navegador só deixa de guardar
    if (((user.layoutPrefs ?? {}) as Record<string, unknown>)['wi.colunas']) return
    try {
      const adotado = adotarEscolhaAntiga(JSON.parse(legado), catalogo)
      if (adotado) setTabela(adotado)
    } catch {
      // escolha ilegível no armazenamento não pode derrubar a tela
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * A lista de colunas é reconciliada com o catálogo A CADA RENDER, e não só
   * quando a preferência é lida.
   *
   * Os campos complementares chegam depois (são outra consulta): lendo a
   * preferência uma vez só, a tabela ficava com o catálogo de fábrica para
   * sempre e as colunas dos campos nunca apareciam no menu.
   */
  const colunasDaPessoa = useMemo(
    () => adotarTabela(tabela, catalogo, catalogoIncompleto)?.cols ?? tabela.cols,
    [tabela, catalogo, catalogoIncompleto],
  )

  /**
   * A VISÃO EM USO (SPEC-20261103-1418) manda nas colunas, na ordenação e no
   * tipo; mexer neles grava na visão — quando a pessoa pode editá-la. Quem só
   * usa a visão mexe na própria tela: o ajuste vale até trocar de visão, e a
   * visão dos outros fica como está. Na lista de Itens a visão é a escolhida
   * no combo; no quadro, quem diz é o quadro.
   */
  const { alterar: alterarVisao } = useMutacoesDeVisao(null)
  const visaoDaTela: VisaoNaLista | null = controlesDoQuadro
    ? (controlesDoQuadro.visao ?? null)
    : visaoEscolhida
      ? {
          id: visaoEscolhida.id,
          colunas: visaoEscolhida.config.colunas,
          ordenacao: visaoEscolhida.config.ordenacao,
          modo: visaoEscolhida.config.modo,
          alterar: visaoEscolhida.canEdit
            ? (config) =>
                alterarVisao.mutate(
                  { id: visaoEscolhida.id, config },
                  { onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível gravar na visão.' }) },
                )
            : null,
        }
      : null
  const [ajusteLocal, setAjusteLocal] = useState<({ id: string } & Partial<ConfigDaVisao>) | null>(null)
  const visaoEmUso: VisaoNaLista | null = visaoDaTela
    ? (() => {
        const ajuste = ajusteLocal?.id === visaoDaTela.id ? ajusteLocal : null
        return {
          ...visaoDaTela,
          ...(ajuste && 'colunas' in ajuste ? { colunas: ajuste.colunas } : {}),
          ...(ajuste && 'ordenacao' in ajuste ? { ordenacao: ajuste.ordenacao } : {}),
          ...(ajuste && 'modo' in ajuste ? { modo: ajuste.modo } : {}),
          alterar:
            visaoDaTela.alterar ??
            ((config) => {
              if (!ajuste) toast.push({ tone: 'info', message: 'Você só usa esta visão: a mudança vale nesta tela, sem gravar.' })
              setAjusteLocal({ ...(ajuste ?? { id: visaoDaTela.id }), ...config })
            }),
        }
      })()
    : null
  /** Colunas próprias da visão em uso, quando ela tem. */
  const filtroComColunas: { colunas: readonly string[]; alterar: (colunas: string[]) => void } | null =
    visaoEmUso?.colunas ? { colunas: visaoEmUso.colunas, alterar: (colunas) => visaoEmUso.alterar!({ colunas }) } : null
  const colunas = useMemo(
    () => (filtroComColunas ? aplicarColunasDoFiltro(colunasDaPessoa, filtroComColunas.colunas) : colunasDaPessoa),
    // a lista do filtro muda de identidade a cada render; o conteúdo é o que conta
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colunasDaPessoa, filtroComColunas?.colunas.join('|')],
  )

  /**
   * SEM FILTRO, as colunas "para todos" (SPEC-20261103-1217): do quadro, dentro
   * de um; do workspace, fora. Chegam a quem abre a lista uma vez — quem
   * esconde uma delas continua escondendo.
   */
  const paraTodos = useColunasParaTodos(kanbanBoardId ?? null)
  useEffect(() => {
    if (paraTodos.colunas === null || catalogoIncompleto) return
    const recebido = receberParaTodos(colunasDaPessoa, tabela.todos, paraTodos.colunas)
    if (recebido.mudou) setTabela({ ...tabela, cols: recebido.cols, todos: recebido.recebidas })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paraTodos.colunas, catalogoIncompleto, colunasDaPessoa])
  /** A pergunta "para quem?" em aberto: a coluna e se ela entrou ou saiu. */
  const [paraQuem, setParaQuem] = useState<{ k: ColunaKey; acao: 'por' | 'tirar' } | null>(null)
  /** As que entram na grade, na ordem — Título é a primeira e não se esconde. */
  const ligadas = colunas.filter((c) => c.on)
  /** As do corpo da linha: o Título é desenhado à parte, com a árvore dentro. */
  const visiveis = ligadas.filter((c) => c.k !== 'titulo')

  /** As do menu: todas menos o Título, filtradas pela busca. */
  const colunasDoMenu = colunas.slice(1).filter((c) => {
    const termo = buscaColuna.trim().toLowerCase()
    if (termo === '') return true
    return rotuloDaColuna(c.k, catalogo).toLowerCase().includes(termo)
  })

  // parte da lista RECONCILIADA: mexer numa coluna grava também as que
  // acabaram de aparecer no catálogo
  const trocarColunas = (fn: (atual: EstadoColuna[]) => EstadoColuna[]) => {
    const novas = fn(colunas)
    if (!filtroComColunas) return setTabela({ ...tabela, cols: novas })
    // com filtro: a largura continua da pessoa; quais e em que ordem vão para o filtro
    const larguras = new Map(novas.map((c) => [c.k, c.w]))
    const daPessoa = colunasDaPessoa.map((c) => ({ ...c, w: larguras.get(c.k) ?? c.w }))
    if (daPessoa.some((c, i) => c.w !== colunasDaPessoa[i]!.w)) setTabela({ ...tabela, cols: daPessoa })
    const doFiltro = colunasLigadas(novas)
    if (doFiltro.join('|') === colunasLigadas(colunas).join('|')) return
    filtroComColunas.alterar(doFiltro)
  }

  /**
   * Ligar ou desligar pelo menu. Sem filtro, quem pode pôr para todos responde
   * "para quem?" (pedido do Cristian, 2026-11-03) — a coluna já aparece, a
   * pergunta só decide se ela chega aos outros também.
   */
  const alternarColuna = (k: ColunaKey) => {
    const ligando = !colunas.find((c) => c.k === k)?.on
    trocarColunas((atual) => atual.map((x) => (x.k === k ? { ...x, on: !x.on } : x)))
    if (filtroComColunas || !paraTodos.podePublicar || paraTodos.colunas === null) return
    if (ligando && !paraTodos.colunas.includes(k)) setParaQuem({ k, acao: 'por' })
    if (!ligando && paraTodos.colunas.includes(k)) setParaQuem({ k, acao: 'tirar' })
  }
  const responderParaQuem = (todos: boolean) => {
    const pergunta = paraQuem
    setParaQuem(null)
    if (!pergunta || !todos || paraTodos.colunas === null) return
    const lista =
      pergunta.acao === 'por'
        ? [...paraTodos.colunas, pergunta.k]
        : paraTodos.colunas.filter((k) => k !== pergunta.k)
    paraTodos.gravar.mutate(lista, {
      onSuccess: () =>
        toast.push({
          tone: 'success',
          message:
            pergunta.acao === 'por'
              ? `“${rotuloDaColuna(pergunta.k, catalogo)}” agora aparece para todos.`
              : `“${rotuloDaColuna(pergunta.k, catalogo)}” saiu para todos.`,
        }),
      onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível gravar.' }),
    })
  }

  const moverColuna = (k: ColunaKey, passo: -1 | 1) =>
    trocarColunas((atual) => moverNaLista(atual, k, passo))

  /** Larguras: durante o arraste vale a largura em curso, que não foi gravada. */
  const [arrastando, setArrastando] = useState<{ k: ColunaKey; w: number } | null>(null)
  const larguraDaColuna = (k: ColunaKey) =>
    arrastando?.k === k
      ? arrastando.w
      : colunas.find((c) => c.k === k)?.w ?? definicaoDaColuna(k, catalogo)?.largura ?? 120

  /**
   * Arraste da borda. A largura em curso fica num estado à parte e só vira
   * preferência ao soltar: gravar a cada pixel encheria a fila de PATCH e
   * redesenharia a lista inteira dezenas de vezes por arraste.
   */
  const arraste = useRef<{ k: ColunaKey; x0: number; w0: number } | null>(null)
  const iniciarArraste = (k: ColunaKey) => (e: EventoDePonteiro<HTMLSpanElement>) => {
    e.preventDefault()
    e.stopPropagation()
    arraste.current = { k, x0: e.clientX, w0: larguraDaColuna(k) }
    setArrastando({ k, w: larguraDaColuna(k) })
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const seguirArraste = (e: EventoDePonteiro<HTMLSpanElement>) => {
    const a = arraste.current
    if (!a) return
    const minimo = definicaoDaColuna(a.k, catalogo)?.minimo ?? 80
    setArrastando({ k: a.k, w: Math.max(minimo, Math.round(a.w0 + e.clientX - a.x0)) })
  }
  const soltarArraste = (e: EventoDePonteiro<HTMLSpanElement>) => {
    const a = arraste.current
    arraste.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (!a || !arrastando) return setArrastando(null)
    const w = arrastando.w
    setArrastando(null)
    trocarColunas((atual) => atual.map((c) => (c.k === a.k ? { ...c, w } : c)))
  }

  /**
   * Reposicionar arrastando o cabeçalho. O Título não é pego nem recebe largada
   * antes de si: ele é a âncora da linha.
   *
   * Por ponteiro, e não pelo arraste nativo do navegador (SPEC-20261103-1217):
   * o nativo parou de mudar a coluna de lugar — ver `lib/arrastar-coluna.ts`.
   */
  const cabecaRef = useRef<HTMLDivElement>(null)
  const [arrastada, setArrastada] = useState<ColunaKey | null>(null)
  const [alvoSolta, setAlvoSolta] = useState<ColunaKey | null>(null)
  const pegarColuna = (k: ColunaKey) => (e: EventoDePonteiro<HTMLSpanElement>) =>
    iniciarArrasteDeColuna(e, {
      chave: k,
      caixa: cabecaRef.current,
      aceita: (destino) => destino !== 'titulo',
      ignorar: '.wi-table__puxador, .wi-table__tudo',
      onInicio: setArrastada,
      onSobre: setAlvoSolta,
      onFim: () => {
        setArrastada(null)
        setAlvoSolta(null)
      },
      onSoltar: (destino) => trocarColunas((atual) => moverParaOLugarDe(atual, k, destino)),
    })

  // a visão com ordenação própria manda; sem ela, a da pessoa
  const ordenacao = visaoEmUso && visaoEmUso.ordenacao !== undefined ? visaoEmUso.ordenacao : tabela.ord
  const alternarOrdem = (k: ColunaKey) => {
    const proxima = proximaOrdenacao(ordenacao, k)
    if (visaoEmUso) return visaoEmUso.alterar!({ ordenacao: proxima })
    setTabela({ ...tabela, ord: proxima })
  }
  const { id: idDaRota = null } = useParams()
  /**
   * Embutida, a rota é do QUADRO: o `:id` dali é o id do quadro, não de um
   * item. Ler aquilo abriria a edição de um item que não existe.
   */
  const selectedId = embutido ? null : idDaRota

  /**
   * Onde um item abre para edição (SPEC-20261006-1351-item-abre-ao-lado-do-explorer):
   * DENTRO de Trabalho, ao lado do explorer — pela lista do quadro, volta ao
   * quadro; pela lista de Itens, volta para ela. A tela antiga de Itens de
   * Trabalho, com a lista própria à esquerda, não existe mais.
   */
  const rotaDoItem = (id: string) =>
    kanbanBoardId ? `/kanbans/${kanbanBoardId}/items/${id}` : `/kanbans/items/${id}`
  const rotaDaLista = kanbanBoardId ? `/kanbans/${kanbanBoardId}` : '/kanbans/items'
  const t = useT()
  const bootstrap = useBootstrap()

  const user = useSession()
  const toast = useToast()
  const queryClient = useQueryClient()
  // o explorer abre em ARVORE por padrao (hierarquia dos itens)
  const [view, setView] = useState<'list' | 'tree'>('tree')
  /** Tabela ou linha do tempo (SPEC-20261002-1241-linha-do-tempo-de-itens) — fica no perfil, como as colunas. */
  const [modoDaLista, setModoDaLista] = usePreferenciaDeLayout<string>('wi.modo', 'tabela')
  const linhaDoTempo = modo
    ? modo === 'tempo'
    : visaoEmUso?.modo
      ? visaoEmUso.modo === 'tempo'
      : modoDaLista === 'tempo'
  /** Trocar o tipo: na visão em uso, grava nela; sem visão, no perfil. */
  const trocarModoDaLista = (novo: 'tabela' | 'tempo') => {
    setModoDaLista(novo)
    if (visaoEmUso) visaoEmUso.alterar!({ modo: novo === 'tempo' ? 'tempo' : 'lista' })
  }
  const [search, setSearch] = useState('')
  // o que está recolhido vai para o PERFIL e volta igual, mesmo saindo da tela
  // (SPEC-20261008-1632-arvore-recolhida-no-perfil)
  const [arvoreGuardada, setArvoreGuardada] = usePreferenciaEstruturada<ArvoreGuardada>(
    'wi.arvore',
    ARVORE_PADRAO,
    adotarArvore,
  )
  const collapsedNodes = useMemo(() => recolhidosDe(arvoreGuardada), [arvoreGuardada])
  // o estado vem do perfil a cada render: a próxima mudança parte do último
  const arvoreRef = useRef(arvoreGuardada)
  arvoreRef.current = arvoreGuardada
  const alternarRecolhido = (id: string) => setArvoreGuardada(alternarNaArvore(arvoreRef.current, id))
  const [trail, setTrail] = useState<string[]>([])
  const [criandoItem, setCriandoItem] = useState(false)
  // piloto do controle de acesso (SPEC-20260814-1444): sem a acao, o botao some
  const podeCriar = usePermission('work-items.item.create')

  // Criar item = abrir a MESMA tela de edição do detalhe (pedido 2026-08-26).
  // O "+" cria um item minimo com os padroes dos catalogos e navega para o
  // detalhe, onde tudo e editado inline; `?novo=1` foca e seleciona o titulo
  // para o usuario digitar por cima do provisorio.
  const criarEAbrir = async (parentId: string | null = null) => {
    const dados = bootstrap.data
    if (!dados || criandoItem) return
    const tipo = dados.workItemTypes.find((r) => r.isActive)?.id
    const status = dados.workItemStatuses.find((s) => s.isActive && s.isInitial)?.id ?? dados.workItemStatuses.find((s) => s.isActive)?.id
    const prioridades = dados.priorities.filter((p) => p.isActive)
    const prioridade = prioridades[1]?.id ?? prioridades[0]?.id
    if (!tipo || !status || !prioridade) return
    setCriandoItem(true)
    try {
      const resposta = await api['work-items'].$post({
        json: {
          title: 'Novo Item de Trabalho',
          description: '',
          workItemTypeId: tipo,
          workItemStatusId: status,
          workItemPriorityId: prioridade,
          ...(parentId ? { parentWorkItemId: parentId } : {}),
          // na Lista de um quadro, vale a visibilidade dele (SPEC-20261007-0934-visibilidade-padrao-do-quadro)
          ...(kanbanBoardId ? { kanbanBoardId } : {}),
        } as never,
      })
      const corpo = (await resposta.json()) as { item?: { id: string }; error?: { message?: string } }
      if (!resposta.ok || !corpo.item) throw new Error(corpo.error?.message ?? 'Não foi possível criar o item.')
      await queryClient.invalidateQueries({ queryKey: ['work-items'] })
      setTrail([])
      navigate(`${rotaDoItem(corpo.item.id)}?novo=1`)
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível criar o item.' })
    } finally {
      setCriandoItem(false)
    }
  }

  /**
   * Escolha de template na criação (SPEC-20260830-1225): o "+" só pergunta
   * quando existe template de item na biblioteca — senão criaria um passo a
   * mais para quem não usa a funcionalidade.
   */
  const templatesDeItem = useTemplates('work_item')
  const temTemplates = (templatesDeItem.data?.templates.length ?? 0) > 0
  const [escolherTemplate, setEscolherTemplate] = useState(false)

  // /work-items/new continua valendo (link externo): cria e redireciona.
  const naRotaDeCriacao = useLocation().pathname === '/work-items/new'
  const jaDisparou = useRef(false)
  useEffect(() => {
    if (naRotaDeCriacao && !jaDisparou.current && bootstrap.data) {
      jaDisparou.current = true
      void criarEAbrir()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naRotaDeCriacao, bootstrap.data])

  // Explorer recolhivel/redimensionavel — largura persiste entre navegacoes.
  const [explorerWidth, setExplorerWidth] = useState(() => {
    const salvo = Number(localStorage.getItem('ob-wi-expl-w'))
    return Number.isFinite(salvo) && salvo >= 240 ? salvo : 340
  })
  const [explorerCollapsed, setExplorerCollapsed] = useState(false)
  // no celular o explorador nunca fica recolhido: ele se reveza com o conteúdo
  const compacto = useCompacto()
  const explorerRef = useRef<HTMLElement>(null)
  const resizing = useRef(false)

  useEffect(() => {
    localStorage.setItem('ob-wi-expl-w', String(explorerWidth))
  }, [explorerWidth])

  useEffect(() => {
    localStorage.setItem('ob-wi-resumo-w', String(resumoWidth))
  }, [resumoWidth])

  useEffect(() => {
    localStorage.setItem('ob-wi-resumo-fix', resumoFixado ? '1' : '0')
  }, [resumoFixado])

  /*
   * Clicar FORA recolhe — mas só com o painel solto, e nunca enquanto se
   * arrasta a divisória: soltar o mouse fora do painel fecharia o que a
   * pessoa estava justamente redimensionando.
   */
  useEffect(() => {
    if (resumoFixado || resumoId === null) return
    const aoClicar = (e: MouseEvent) => {
      if (resizing.current) return
      const alvo = e.target as Node
      if (resumoRef.current?.contains(alvo)) return
      // clique na própria lista troca o item; não é "fora"
      if ((alvo as HTMLElement).closest?.('.wi-table__row')) return
      setResumoId(null)
    }
    document.addEventListener('mousedown', aoClicar)
    return () => document.removeEventListener('mousedown', aoClicar)
  }, [resumoFixado, resumoId])

  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      if (!resizing.current) return
      /*
       * A divisória dimensiona o PAINEL (SPEC-20261002-1541), medindo da borda
       * direita para dentro. Antes ela dava largura ao explorer, que hoje é a
       * área principal e cresce sozinho com o que sobra.
       */
      const left = explorerRef.current?.getBoundingClientRect().left ?? 0
      const maxPainel = window.innerWidth - left - 320
      setResumoWidth(Math.max(300, Math.min(maxPainel, window.innerWidth - event.clientX)))
    }
    const onUp = () => {
      resizing.current = false
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  /** Junta linhas dentro de cada checklist, no formato que o cronograma espera. */
  const montarChecklists = (dados: {
    checklists?: { id: string; workItemId: string; name: string }[]
    lines?: {
      id: string
      checklistId: string
      text: string
      isDone: boolean
      sortOrder: number
      startAt: string | null
      dueAt: string | null
      linkedWorkItemId: string | null
    }[]
    items?: { id: string; code: string; title: string; startAt: string | null; dueAt: string | null }[]
  } | undefined) => {
    if (!dados?.checklists) return []
    const porItem = new Map((dados.items ?? []).map((i) => [i.id, i]))
    return dados.checklists.map((cl) => ({
      ...cl,
      items: (dados.lines ?? [])
        .filter((l) => l.checklistId === cl.id)
        .map((l) => {
          const alvo = l.linkedWorkItemId ? porItem.get(l.linkedWorkItemId) : undefined
          return {
            ...l,
            linked: alvo
              ? { id: alvo.id, code: alvo.code, title: alvo.title, startAt: alvo.startAt, dueAt: alvo.dueAt }
              : null,
          }
        }),
    }))
  }

  // TODOS os itens que a pessoa pode ver, em páginas
  // (SPEC-20261006-2244-todos-os-itens-na-lista). A busca roda aqui, com as
  // mesmas regras do cartão — e não no servidor, que só olha título e código
  // e não acharia um item pelo responsável.
  const list = useWorkItemList({ kanbanBoardId, todos: true })
  const detail = useWorkItemDetail(selectedId)
  const resumo = useWorkItemDetail(resumoId)

  /**
   * Cronograma da SUBÁRVORE: itens, checklists e linhas de todos os
   * descendentes numa chamada só. É o que deixa o Gantt abrir checklist dentro
   * de checklist em qualquer profundidade.
   */
  const cronograma = useQuery({
    queryKey: ['work-item', 'schedule', selectedId],
    enabled: selectedId !== null,
    queryFn: async () =>
      (await api['work-items'][':id'].schedule.$get({ param: { id: selectedId! } })).json(),
  })

  /**
   * Datas no cronograma (2026-08-30). A linha de checklist que virou Item de
   * Trabalho grava no ITEM; a que não virou grava na própria linha. Invalidar as
   * duas consultas mantém a lista (barras dos filhos) e o detalhe (checklists)
   * em sincronia.
   */
  const qc = useQueryClient()
  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['work-items'] })
    void qc.invalidateQueries({ queryKey: ['work-item'] })
    void qc.invalidateQueries({ queryKey: ['work-item', 'schedule'] })
  }
  const datasDaLinha = useMutation({
    mutationFn: async (input: { checklistItemId: string; startAt: string | null; dueAt: string | null }) =>
      api['work-items']['checklist-items'][':itemId'].$patch({
        param: { itemId: input.checklistItemId },
        json: { startAt: input.startAt, dueAt: input.dueAt } as never,
      }),
    onSuccess: recarregar,
  })
  /** Troca status/etapa de um item direto no cronograma (2026-08-30). */
  const trocarStatus = useMutation({
    mutationFn: async (input: { itemId: string; statusId: string }) =>
      api['work-items'][':id'].status.$post({ param: { id: input.itemId }, json: { statusId: input.statusId } }),
    onSuccess: recarregar,
    onError: (e) =>
      toast.push({
        tone: 'danger',
        message: e instanceof Error ? e.message : 'Falha ao mudar o status.',
      }),
  })
  /**
   * Checkbox das colunas da TRILHA (SPEC-20261007-2155-trilha-de-etapas-e-status): marcar/desmarcar uma
   * etapa ou um status. As regras moram no servidor; aqui só se pede e se
   * recarrega — e o motivo da recusa vira aviso.
   */
  type PedidoDaTrilha = {
    itemId: string
    codigo: string
    alvo: 'etapa' | 'status'
    id: string
    marcar: boolean
    comment?: string
    /** Confirmado na tela de concluir em cadeia. */
    cascata?: boolean
  }
  /** Reabertura com coisas concluídas embaixo: só este item, ou tudo. */
  const [reabrindoEmCadeia, setReabrindoEmCadeia] = useState<
    { pedido: PedidoDaTrilha; titulo: string; nos: NoDaCascata[] | null } | null
  >(null)
  /**
   * Um checkbox de STATUS que REABRE o item (sai de um status concluído)
   * pergunta antes, com a MESMA tela da edição do item: "Reabrir só este item"
   * ou "Reabrir tudo" — quando há filhos ou atividades concluídos embaixo
   * (pedido do Cristian, 2026-10-08). Sem nada embaixo, segue direto.
   */
  const pedirNaTrilha = async (row: (typeof allItems)[number], pedido: PedidoDaTrilha) => {
    const destino = (bootstrap.data?.workItemStatuses ?? []).find((s) => s.id === pedido.id)
    const reabre =
      pedido.alvo === 'status' &&
      row.status.isTerminal &&
      (pedido.marcar ? destino !== undefined && !destino.isTerminal : pedido.id === row.status.id)
    if (!reabre) {
      marcarTrilha.mutate(pedido)
      return
    }
    let nos: NoDaCascata[] | null = null
    try {
      const resposta = await api['work-items'][':id'].reabertura.$get({ param: { id: row.id } })
      nos = (await resposta.json()).nos
    } catch {
      // sem a prévia a janela abre mesmo assim, oferecendo só este item
    }
    if (nos !== null && nos.length <= 1) {
      marcarTrilha.mutate(pedido)
      return
    }
    setReabrindoEmCadeia({ pedido, titulo: row.title, nos })
  }
  /** Conclusão que leva junto o que está embaixo, esperando a confirmação. */
  const [concluindoEmCadeia, setConcluindoEmCadeia] = useState<(PedidoDaTrilha & { titulo: string }) | null>(null)
  /** Mudança que reabre o item, esperando o motivo no modal. */
  const [pedindoMotivo, setPedindoMotivo] = useState<(PedidoDaTrilha & { statusDestino: string | null }) | null>(null)
  const marcarTrilha = useMutation({
    mutationFn: async (input: PedidoDaTrilha) => {
      const resposta = await api['work-items'][':id'].trilha.$post({
        param: { id: input.itemId },
        json: {
          alvo: input.alvo,
          id: input.id,
          marcar: input.marcar,
          ...(input.comment ? { comment: input.comment } : {}),
          ...(input.cascata ? { cascata: true } : {}),
        },
      })
      if (!resposta.ok) {
        const corpo = (await resposta.json().catch(() => null)) as {
          error?: { code?: string; message?: string; details?: { statusDestino?: string } }
        } | null
        throw Object.assign(new Error(corpo?.error?.message ?? 'Não foi possível marcar.'), {
          codigo: corpo?.error?.code,
          statusDestino: corpo?.error?.details?.statusDestino ?? null,
        })
      }
      return resposta.json()
    },
    onSuccess: () => {
      setPedindoMotivo(null)
      setConcluindoEmCadeia(null)
      recarregar()
    },
    onError: (e, pedido) => {
      // REABERTURA sem motivo: abre o modal para digitá-lo e confirmar
      // o cliente da API já lança OrbindApiError (code/details) antes da
      // checagem de `resposta.ok`; os dois formatos chegam aqui
      const erro = e as Error & { codigo?: string; statusDestino?: string | null; code?: string; details?: unknown }
      const codigo = erro.codigo ?? erro.code
      // filhos ou atividades em aberto: a MESMA tela da edição do item pergunta
      if (codigo === 'concluir_em_cadeia') {
        const titulo = allItems.find((i) => i.id === pedido.itemId)?.title ?? pedido.codigo
        setConcluindoEmCadeia({ ...pedido, titulo })
        return
      }
      if (codigo === 'motivo_obrigatorio') {
        const destino = erro.statusDestino ?? (erro.details as { statusDestino?: string } | undefined)?.statusDestino
        setPedindoMotivo({ ...pedido, statusDestino: destino ?? null })
        return
      }
      toast.push({ tone: 'danger', message: erro.message || 'Não foi possível marcar.' })
    },
  })
  const trocarEtapa = useMutation({
    mutationFn: async (input: { itemId: string; flowStepId: string }) =>
      api['work-items'][':id'].$patch({
        param: { id: input.itemId },
        query: {},
        json: { flowStepId: input.flowStepId } as never,
      }),
    onSuccess: recarregar,
    onError: (e) =>
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao mudar a etapa.' }),
  })
  const trocarResponsavel = useMutation({
    mutationFn: async (input: { itemId: string; memberId: string | null }) =>
      api['work-items'][':id'].$patch({
        param: { id: input.itemId },
        query: {},
        json: { assigneeMemberId: input.memberId } as never,
      }),
    onSuccess: recarregar,
    onError: (e) =>
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao trocar o responsável.' }),
  })
  const trocarPrioridade = useMutation({
    mutationFn: async (input: { itemId: string; priorityId: string }) =>
      api['work-items'][':id'].$patch({
        param: { id: input.itemId },
        query: {},
        json: { workItemPriorityId: input.priorityId } as never,
      }),
    onSuccess: recarregar,
    onError: (e) =>
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao mudar a prioridade.' }),
  })
  // As etapas dos fluxos do workspace, para o menu da linha, são a consulta
  // `nosDeFluxo` lá do começo — a mesma das colunas da trilha.

  const datasDoChecklist = useMutation({
    mutationFn: async (input: { checklistId: string; startAt: string | null; dueAt: string | null }) =>
      api['work-items'].checklists[':checklistId'].$patch({
        param: { checklistId: input.checklistId },
        json: { startAt: input.startAt, dueAt: input.dueAt } as never,
      }),
    onSuccess: recarregar,
  })
  const datasDoItem = useMutation({
    mutationFn: async (input: { itemId: string; startAt: string | null; dueAt: string | null }) =>
      api['work-items'][':id'].$patch({
        param: { id: input.itemId },
        query: {},
        json: { startAt: input.startAt, dueAt: input.dueAt } as never,
      }),
    onSuccess: recarregar,
  })
  const [searchParams] = useSearchParams()

  // Opcoes dos seletores do editor de filtro rapido — dos catalogos do workspace.
  const rotulo = (row: { code: string; nameTranslationKey?: string | null }) => {
    const key = row.nameTranslationKey ?? `x.${row.code}`
    const label = t(key)
    return label === key ? row.code : label
  }
  const statusOptions = useMemo(
    () => (bootstrap.data?.workItemStatuses ?? []).map((s) => ({ value: s.id, label: rotulo(s) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bootstrap.data],
  )
  const typeOptions = useMemo(
    () => (bootstrap.data?.workItemTypes ?? []).filter((x) => x.isActive).map((x) => ({ value: x.id, label: rotulo(x) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bootstrap.data],
  )
  const priorityOptions = useMemo(
    () => (bootstrap.data?.priorities ?? []).map((p) => ({ value: p.id, label: rotulo(p) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bootstrap.data],
  )
  const memberOptions = useMemo(
    () => (bootstrap.data?.members ?? []).map((m) => ({ value: m.id, label: m.name })),
    [bootstrap.data],
  )
  // etiquetas do workspace — opcoes do filtro por etiqueta (2026-09-02)
  const etiquetas = useQuery({
    queryKey: ['labels-all'],
    queryFn: async () => (await api['work-items'].labels.all.$get()).json(),
  })
  // Construtor avancado (CAMPO/OPERADOR/VALOR): opcoes de valor e leitura da linha.
  // quadros do workspace — opções do filtro por Quadros (SPEC-20261007-1919-quadros-nos-filtros)
  const quadrosDoWorkspace = useQuery({
    queryKey: ['kanbans'],
    queryFn: async () => (await api.kanbans.$get()).json(),
  })
  const loadValueOptions = useMemo(
    () =>
      fazerLoadValueOptions(bootstrap.data, t, etiquetas.data?.labels, quadrosDoWorkspace.data?.boards, {
        // etapas: as que existem nos itens da lista; portfólio: todos os nós
        etapas: (list.data?.items ?? []).flatMap((i) => (i.stage?.step ? [{ fluxo: i.stage.flow ?? null, etapa: i.stage.step }] : [])),
        portfolios: [...nosDoPortfolio.values()],
      }),
    [bootstrap.data, t, etiquetas.data, quadrosDoWorkspace.data, list.data, nosDoPortfolio],
  )
  const rowValues = useMemo(
    () => fazerRowValues(bootstrap.data, t, (id) => nosDoPortfolio.get(id)),
    [bootstrap.data, t, nosDoPortfolio],
  )
  /**
   * Campos do construtor: os da barra de propriedades e os COMPLEMENTARES
   * visíveis (SPEC-20261008-1745-todos-os-campos-no-filtro) — o quadro já fazia assim.
   */
  const complementaresFiltraveis = useMemo(
    () => (camposComplementares.data?.fields ?? []).filter((f) => f.defaultVisible),
    [camposComplementares.data],
  )
  const camposDoFiltro = useMemo<readonly FilterField[]>(
    () => [
      ...CAMPOS_DE_FILTRO,
      ...complementaresFiltraveis.map((f) => ({
        key: `cf:${f.key}`,
        label: rotuloCampo(f.labels, f.key),
        type: tipoDoCampoComplementar(f.type),
        placeholder: 'Valor…',
      })),
    ],
    [complementaresFiltraveis],
  )
  /** Opções de valor, inclusive dos complementares (as do próprio cadastro do campo). */
  const opcoesDoFiltro = (fieldKey: string, query: string) => {
    if (!fieldKey.startsWith('cf:')) return loadValueOptions(fieldKey, query)
    const campo = complementaresFiltraveis.find((c) => `cf:${c.key}` === fieldKey)
    if (!campo) return []
    const casa = (rotulo: string) => rotulo.toLowerCase().includes(query.trim().toLowerCase())
    if (campo.type === 'boolean') return [{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }].filter((o) => casa(o.label))
    return ((campo.config as { options?: { value: string; label: string }[] }).options ?? []).filter((o) => casa(o.label))
  }

  const open = (id: string, pushTrail = true) => {
    if (pushTrail && selectedId && id !== selectedId) setTrail((current) => [...current, selectedId])
    navigate(rotaDoItem(id))
  }

  const goBack = () => {
    const previous = trail[trail.length - 1]
    setTrail((current) => current.slice(0, -1))
    if (previous) navigate(rotaDoItem(previous))
    else navigate(rotaDaLista)
  }

  const catalogLabel = (row: { code: string; nameTranslationKey?: string | null } | undefined | null) => {
    if (!row) return ''
    const key = row.nameTranslationKey ?? `work_item_status.${row.code.toLowerCase()}.name`
    const label = t(key)
    return label === key ? row.code : label
  }

  const allItems = list.data?.items ?? []
  /*
    A LISTA DO QUADRO IGUAL À GERAL (SPEC-20261009-1240-lista-do-quadro-igual-a-geral,
    pedido do Cristian: "todos os recursos da lista devem ser iguais seja no
    quadro ou seja nos itens de trabalho gerais"). A lista do quadro só traz os
    itens DO quadro; filhos e itens de checklist de fora dele viravam linha
    simples. A árvore usa a lista geral como apoio — o mesmo cache da lista
    geral, então fora do quadro não há consulta a mais.
  */
  const listaGeral = useWorkItemList({ todos: true })
  const universo = useMemo(() => {
    if (!kanbanBoardId) return allItems
    const porId = new Map((listaGeral.data?.items ?? []).map((i) => [i.id, i]))
    for (const item of allItems) porId.set(item.id, item)
    return [...porId.values()]
  }, [kanbanBoardId, allItems, listaGeral.data])
  /** Pais que não vêm na lista (arquivados, privados, fora do recorte) — só para a árvore. */
  const ancestrais = useMemo(
    () =>
      (list.data as { ancestrais?: ((typeof allItems)[number] & { arquivado: boolean; oculto: boolean })[] } | undefined)
        ?.ancestrais ?? [],
    [list.data],
  )
  /**
   * Clicar num item abre o ITEM, mesmo que ele tenha filhos (2026-08-30). O
   * cronograma virou um destino explícito (`?gantt=1`), aberto pelo botão do
   * gráfico na linha do pai — antes o detalhe do pai ficava escondido atrás da
   * lista de filhos.
   */
  const temFilhos = selectedId !== null && allItems.some((i) => i.parentWorkItemId === selectedId)
  const mostrarFilhos = temFilhos && searchParams.get('gantt') === '1'
  const itemPai = mostrarFilhos ? allItems.find((i) => i.id === selectedId) ?? null : null

  const estaAtrasado = (row: (typeof allItems)[number]) =>
    row.dueAt !== null && new Date(row.dueAt) < new Date() && !row.status.isTerminal
  // Aplica a selecao: chip fixo (Todos/Meus/Atrasados) ou um filtro salvo,
  // cujos criterios valem em conjunto (E).
  /**
   * Busca do quadro, com as MESMAS regras do cartão: sem acento e sem caixa,
   * casando código, título, responsável e empresa.
   */
  const semAcento = (texto: string) => texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
  const casaBuscaDoQuadro = (row: (typeof allItems)[number]) => {
    // dentro do quadro, a busca do cabeçalho dele; fora, a desta lista
    const termo = (controlesDoQuadro ? controlesDoQuadro.busca : search).trim()
    if (termo === '') return true
    const empresa = (bootstrap.data?.companies ?? []).find((c) => c.id === row.companyId)?.name ?? ''
    return semAcento([row.code, row.title, row.assignee?.name ?? '', empresa].join(' ')).includes(semAcento(termo))
  }

  const items = allItems.filter((row) => {
    // dentro do quadro manda o cabeçalho dele; o filtro desta lista não aparece
    if (!casaBuscaDoQuadro(row)) return false
    if (controlesDoQuadro) {
      if (controlesDoQuadro.soOsIds && !controlesDoQuadro.soOsIds.has(row.id)) return false
      return controlesDoQuadro.filtros.every((f) => evaluateFilter(rowValues(row), f))
    }
    if (selecao.tipo === 'builtin') {
      if (selecao.id === 'mine') return row.assignee?.id === user.id
      if (selecao.id === 'overdue') return estaAtrasado(row)
      return true
    }
    const c = criteriosEscolhidos ?? selecao.criteria
    if (c.mine && row.assignee?.id !== user.id) return false
    if (c.overdue && !estaAtrasado(row)) return false
    if (c.statusId && row.status.id !== c.statusId) return false
    if (c.typeId && row.type.id !== c.typeId) return false
    if (c.priorityId && row.priority?.id !== c.priorityId) return false
    if (c.assigneeId && row.assignee?.id !== c.assigneeId) return false
    // construtor avancado (campo/operador/valor), quando houver
    if (c.advanced && c.advanced.criteria.length > 0 && !evaluateFilter(rowValues(row), c.advanced)) return false
    return true
  })

  /**
   * Progresso de um item por TUDO o que está debaixo dele (2026-08-30): conta
   * todos os descendentes, não só os filhos diretos — senão um item cujo
   * trabalho real está dois níveis abaixo apareceria sempre em 0%.
   *
   * E os CHECKLISTS (bug de 2026-10-08): o item escolhido num checklist fica
   * VINCULADO sem trocar de pai, e o "Ajustes e melhorias Orbind" — só
   * checklists, com linhas soltas e itens vinculados — aparecia com "0 itens"
   * e 0/0. A regra é a de `@orbind/core`, a mesma do servidor: linha solta
   * conta pela marca, a vinculada pelo status do item, e o item que é filho E
   * está num checklist conta uma vez.
   */
  const progressoDe = useMemo(() => {
    const linhasPorItem = new Map(
      universo.map((row) => [
        row.id,
        ((row as { checklists?: { linhas: LinhaDoProgresso[] }[] }).checklists ?? []).flatMap((c) => c.linhas),
      ]),
    )
    return criarProgressoDosItens(
      universo.map((row) => ({ id: row.id, parentWorkItemId: row.parentWorkItemId, concluido: row.status.isTerminal })),
      (id) => linhasPorItem.get(id) ?? [],
    )
  }, [universo])

  /**
   * Ordenação (SPEC-20261003-0002). Ordena-se pelo que está ESCRITO na célula —
   * o rótulo do catálogo, a data, o percentual —, e não por id ou ordem de
   * cadastro: o que se vê é o que ordena.
   *
   * VAZIO vai para o fim nas duas direções. Inverter a direção não pode fazer
   * "sem prazo" virar o mais urgente: um item sem data não é nem o mais cedo
   * nem o mais tarde, é outra coisa.
   */
  const valorDaColuna = (row: (typeof allItems)[number], k: ColunaKey): string | number | null => {
    switch (k) {
      case 'titulo':
        return row.title
      case 'status':
        return catalogLabel(row.status)
      case 'responsavel':
        return row.assignee?.name ?? null
      case 'prazo':
        return row.dueAt === null ? null : Date.parse(row.dueAt)
      case 'inicio':
        return row.startAt === null ? null : Date.parse(row.startAt)
      case 'progresso': {
        const prog = progressoDe(row.id)
        return prog.total === 0 ? null : prog.feitos / prog.total
      }
      case 'prioridade':
        return row.priority ? catalogLabel(row.priority) : null
      case 'etapa':
        return row.stage?.step ?? null
      case 'conclusao':
        return row.completedAt === null ? null : Date.parse(row.completedAt)
      case 'criado':
        return row.createdAt ? Date.parse(row.createdAt) : null
      case 'atualizado':
        return row.updatedAt ? Date.parse(row.updatedAt) : null
      default: {
        // trilha: marcado antes de desmarcado
        const marca = marcaDaTrilha(row, k)
        if (marca) return marca.marcado ? 1 : 0
        // empresa, área, portfólio, tipo e campos complementares ordenam pelo
        // que está escrito — o que se vê é o que ordena
        return textoSimples(row, k)
      }
    }
  }

  /**
   * Texto das colunas que não têm desenho próprio
   * (SPEC-20261006-1057-colunas-da-lista): empresa, área, portfólio, datas de
   * registro e os campos complementares.
   *
   * Num lugar só, e não espalhado entre a célula e o comparador: foi assim que
   * a grade e as células divergiram da primeira vez.
   */
  const dataCurta = (iso: string) =>
    new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' })
      .format(new Date(iso))
      .replace('.', '')

  /**
   * Campo complementar de DATA chega como texto ISO (`2026-09-30T03:00:00.000Z`)
   * e aparecia assim, cru, na coluna. Data sem hora é formatada pelos números,
   * sem passar por Date: `new Date('2026-09-30')` é meia-noite UTC, que no
   * Brasil vira o dia 29.
   */
  const dataDeCampo = (texto: string) => {
    const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto)
    if (soData) return `${soData[3]}/${soData[2]}/${soData[1]}`
    if (/^\d{4}-\d{2}-\d{2}T/.test(texto)) {
      const d = new Date(texto)
      if (!Number.isNaN(d.getTime())) return new Intl.DateTimeFormat('pt-BR').format(d)
    }
    return texto
  }

  /**
   * O estado do checkbox de uma coluna da TRILHA (SPEC-20261007-2155-trilha-de-etapas-e-status).
   * Marcado = já passou (ou está). Pode mexer: na etapa, marcar só a próxima
   * e desmarcar só a atual; no status, marcar qualquer um e desmarcar só o
   * atual — o servidor confere de novo, e diz o porquê quando recusa.
   */
  const marcaDaTrilha = (row: (typeof allItems)[number], k: ColunaKey) => {
    const trilha = (row as { trilha?: { etapas: string[]; status: string[]; proximas: string[] } }).trilha
    if (!trilha) return null
    if (k.startsWith(PREFIXO_ETAPA)) {
      const id = k.slice(PREFIXO_ETAPA.length)
      const marcado = trilha.etapas.includes(id)
      const atual = row.flowStepId === id
      return {
        alvo: 'etapa' as const,
        id,
        marcado,
        habilitado: marcado ? atual : trilha.proximas.includes(id),
        motivo: marcado
          ? atual
            ? 'Desmarcar volta o item para a etapa anterior, se o fluxo permitir'
            : 'O item já passou por esta etapa'
          : trilha.proximas.includes(id)
            ? 'Marcar leva o item para esta etapa'
            : 'Só a próxima etapa do fluxo pode ser marcada',
      }
    }
    if (k.startsWith(PREFIXO_STATUS)) {
      const id = k.slice(PREFIXO_STATUS.length)
      const marcado = trilha.status.includes(id)
      const atual = row.status.id === id
      return {
        alvo: 'status' as const,
        id,
        marcado,
        habilitado: !marcado || atual,
        motivo: marcado
          ? atual
            ? 'Desmarcar volta o item para o status anterior'
            : 'O item já passou por este status'
          : 'Marcar muda o item para este status',
      }
    }
    return null
  }

  const textoSimples = (row: (typeof allItems)[number], k: ColunaKey): string | null => {
    if (k.startsWith(PREFIXO_COMPLEMENTAR)) {
      const valor = (row.customValues as Record<string, unknown> | null)?.[
        k.slice(PREFIXO_COMPLEMENTAR.length)
      ]
      if (valor === null || valor === undefined || valor === '') return null
      if (Array.isArray(valor)) return valor.join(', ')
      if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não'
      return dataDeCampo(String(valor))
    }
    switch (k) {
      case 'tipo':
        return catalogLabel(row.type) || null
      case 'empresa':
        return (bootstrap.data?.companies ?? []).find((c) => c.id === row.companyId)?.name ?? null
      case 'area': {
        // a area tem nome traduzido, como os demais catalogos
        const area = (bootstrap.data?.areas ?? []).find((a) => a.id === row.companyAreaId)
        if (!area) return null
        const rotulo = t(area.nameTranslationKey)
        return rotulo === area.nameTranslationKey ? area.code : rotulo
      }
      case 'portfolio':
        return nosDoPortfolio.get(row.portfolioNodeId ?? '') ?? null
      case 'conclusao':
        return row.completedAt ? dataCurta(row.completedAt) : null
      case 'criado':
        return row.createdAt ? dataCurta(row.createdAt) : null
      case 'atualizado':
        return row.updatedAt ? dataCurta(row.updatedAt) : null
      default:
        return null
    }
  }

  const comparador = useMemo(
    () => fazerComparador(ordenacao, valorDaColuna, (row) => row.code),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordenacao, allItems, t],
  )

  /** Arvore montada no cliente a partir de parentWorkItemId da lista. */
  type LinhaDeChecklist = {
    id: string
    text: string
    isDone: boolean
    linkedWorkItemId: string | null
    /** Código e título do item vinculado — a linha não tem texto próprio. */
    vinculado?: { id: string; code: string; title: string } | null
  }
  type ChecklistDoItem = { id: string; name: string; linhas: LinhaDeChecklist[] }
  const checklistsDe = (row: (typeof items)[number]) =>
    ((row as { checklists?: ChecklistDoItem[] }).checklists ?? []).filter((c) => c.linhas.length > 0)
  /** Chave de recolher de um checklist — não colide com id de item. */
  const chaveDoChecklist = (id: string) => `cl:${id}`

  /**
   * A árvore em NÓS (SPEC-20261007-2212-checklist-na-lista-de-itens): o item; debaixo dele, cada CHECKLIST,
   * e dentro do checklist as LINHAS na ordem — a linha solta com checkbox, a
   * que virou item mostrando o próprio item (com a árvore dele). Os filhos que
   * não estão em checklist nenhum vêm depois, como sempre.
   */
  type NoDaArvore =
    | {
        tipo: 'checklist'
        /** Chave da OCORRÊNCIA (o mesmo checklist pode aparecer em dois lugares). */
        id: string
        /** Chave de recolher/expandir — a mesma em todas as ocorrências. */
        chaveDeRecolher: string
        nome: string
        depth: number
        raiz: string
        feitos: number
        total: number
      }
    | {
        tipo: 'linha'
        id: string
        linhaId: string
        texto: string
        feito: boolean
        depth: number
        raiz: string
        /** Item escolhido no checklist que NÃO é filho deste: a linha aponta para ele. */
        vinculado: { id: string; code: string; title: string } | null
      }
  /**
   * A árvore da lista (SPEC-20261008-1226-arvore-recursiva-e-avulsos, pedido do Cristian em 2026-10-08):
   * "quando um item filho tiver outros itens filhos ou outros checklists deve
   * ser feita a recursividade listando todos ... item que estejam dentro de
   * outro item não devem ficar em itens avulsos".
   *
   * - O que está DE CIMA de um item é o pai; sem pai, o item que o tem num
   *   checklist (o vínculo não troca o pai, mas o item está dentro do outro).
   * - Com filtro ativo, quem passou no filtro continua debaixo de quem está
   *   em cima dele: os de cima entram como CONTEXTO (esmaecidos). Antes o
   *   filho cujo pai não passava no filtro virava raiz — e caía nos avulsos.
   * - Item vinculado num checklist abre a PRÓPRIA árvore ali dentro (filhos e
   *   checklists dele), não só uma linha apontando para ele. Como o mesmo item
   *   pode aparecer em mais de um lugar, cada ocorrência tem a sua chave, e o
   *   caminho percorrido barra ciclo (A no checklist de B, B no de A).
   */
  const { tree, nos, linhasDaArvore } = useMemo(() => {
    // status de TODOS os itens, e não só dos filhos: o item escolhido num
    // checklist continua debaixo do pai de origem
    const concluidoPorId = new Map(universo.map((i) => [i.id, { concluido: i.status.isTerminal }]))
    // nome do item vinculado: o servidor manda na linha; na falta (resposta
    // antiga), sai da própria lista
    const nomePorId = new Map(universo.map((i) => [i.id, { id: i.id, code: i.code, title: i.title }]))
    // os pais que faltavam na lista também valem para subir a cadeia
    const todosPorId = new Map([...ancestrais, ...universo].map((i) => [i.id, i]))
    // quem tem cada item num checklist (pode ser mais de um)
    const donosDoVinculo = new Map<string, string[]>()
    for (const dono of universo) {
      for (const checklist of checklistsDe(dono)) {
        for (const linha of checklist.linhas) {
          if (!linha.linkedWorkItemId || linha.linkedWorkItemId === dono.id) continue
          const donos = donosDoVinculo.get(linha.linkedWorkItemId) ?? []
          if (!donos.includes(dono.id)) donos.push(dono.id)
          donosDoVinculo.set(linha.linkedWorkItemId, donos)
        }
      }
    }
    const deCima = (id: string): string | null => {
      const item = todosPorId.get(id)
      if (!item) return null
      return item.parentWorkItemId ?? donosDoVinculo.get(id)?.[0] ?? null
    }
    // visíveis = os que passaram no filtro + quem está em cima deles
    const filtrados = new Set(items.map((i) => i.id))
    const visiveis = new Set(filtrados)
    for (const item of items) {
      const subindo = new Set([item.id])
      let acima = deCima(item.id)
      while (acima && todosPorId.has(acima) && !subindo.has(acima)) {
        subindo.add(acima)
        visiveis.add(acima)
        acima = deCima(acima)
      }
    }

    type Row = (typeof items)[number] & {
      children: Row[]
      depth: number
      raiz: string
      tipo: 'item'
      /** Chave desta OCORRÊNCIA na árvore (o vinculado aparece também no checklist). */
      chave: string
      /** Não passou no filtro: está aqui só para manter a hierarquia. */
      contexto: boolean
    }
    const byId = new Map<string, Row>()
    for (const id of visiveis) {
      const item = todosPorId.get(id)
      if (!item) continue
      byId.set(id, { ...item, children: [], depth: 0, raiz: id, tipo: 'item', chave: id, contexto: !filtrados.has(id) })
    }
    const roots: Row[] = []
    for (const row of byId.values()) {
      const parent = row.parentWorkItemId ? byId.get(row.parentWorkItemId) : undefined
      if (parent) {
        parent.children.push(row)
        continue
      }
      // sem pai à vista, mas dentro do checklist de um item à vista: aparece lá
      const dentroDeOutro = (donosDoVinculo.get(row.id) ?? []).some((dono) => byId.has(dono))
      if (!dentroDeOutro) roots.push(row)
    }

    /*
      A ÁRVORE COMPLETA de cada item, sem o recorte do filtro (pedido do
      Cristian, 2026-10-08: "para os itens filhos mostrar as mesmas colunas com
      seus dados, e se eles tiverem filhos ou checklist poder expandir e assim
      sucessivamente"). O item que está DENTRO de um checklist é conteúdo do
      dono: mesmo fora do filtro aparece como linha inteira (esmaecida), com a
      árvore dele — antes virava uma linha simples, sem colunas nem expandir.
    */
    const completos = new Map<string, Row>()
    for (const item of todosPorId.values()) {
      completos.set(item.id, { ...item, children: [], depth: 0, raiz: item.id, tipo: 'item', chave: item.id, contexto: !filtrados.has(item.id) })
    }
    for (const row of completos.values()) {
      const pai = row.parentWorkItemId ? completos.get(row.parentWorkItemId) : undefined
      if (pai) pai.children.push(row)
    }

    /*
      COM FILTRO (ou busca) ativo, ele vale também para os FILHOS (pedido do
      Cristian, 2026-10-10: "preciso que o filtro não seja somente para os pais,
      que ele filtre os itens filhos também"): o item de checklist que não passa
      some — só aparece quem passa ou quem está no caminho de quem passa (o
      contexto, esmaecido). As linhas soltas do checklist de quem PASSOU são o
      conteúdo dele e ficam; no item que só está ali como caminho, somem — e o
      checklist dele sem nada à vista some inteiro. Sem filtro, a árvore
      completa (inclusive o que é de fora do quadro) aparece como antes.
    */
    const filtroAtivo = items.length < allItems.length

    const flat: Row[] = []
    const todos: (Row | NoDaArvore)[] = []
    const walk = (rows: Row[], depth: number, raiz: string | null, caminho: readonly string[], prefixo: string) => {
      // ordena entre IRMÃOS: ordenar a lista inteira desfaria a hierarquia,
      // e um filho apareceria longe do seu pai
      for (const base of [...rows].sort(comparador)) {
        // ciclo (A no checklist de B e B no de A): para no segundo encontro
        if (caminho.includes(base.id)) continue
        const row: Row = { ...base, depth, raiz: raiz ?? base.id, chave: `${prefixo}${base.id}` }
        flat.push(row)
        todos.push(row)
        if (collapsedNodes.has(row.id)) continue
        const abaixo = [...caminho, row.id]
        const prefixoAbaixo = `${row.chave}>`
        const filhoPorId = new Map(row.children.map((c) => [c.id, c]))
        const noChecklist = new Set<string>()
        for (const checklist of checklistsDe(row)) {
          const chave = chaveDoChecklist(checklist.id)
          const feitos = checklist.linhas.filter((l) =>
            linhaDeChecklistFeita(l, l.linkedWorkItemId ? concluidoPorId.get(l.linkedWorkItemId) : null),
          ).length
          // com filtro, o checklist sem nenhum item à vista nem aparece
          if (
            filtroAtivo &&
            row.contexto &&
            !checklist.linhas.some(
              (l) => l.linkedWorkItemId && (filhoPorId.has(l.linkedWorkItemId) || byId.has(l.linkedWorkItemId)),
            )
          ) {
            // ainda assim, o filho que mora nele não sai de novo como filho solto
            for (const l of checklist.linhas) if (l.linkedWorkItemId) noChecklist.add(l.linkedWorkItemId)
            continue
          }
          todos.push({ tipo: 'checklist', id: `${prefixoAbaixo}${chave}`, chaveDeRecolher: chave, nome: checklist.name, depth: depth + 1, raiz: row.raiz, feitos, total: checklist.linhas.length })
          const fechado = collapsedNodes.has(chave)
          for (const linha of checklist.linhas) {
            if (linha.linkedWorkItemId) {
              // o item vinculado está à vista: abre a ÁRVORE dele aqui dentro
              // à vista no recorte, ou da árvore completa (fora do filtro, esmaecido)
              // (com filtro, só quem está à vista: a árvore completa não entra)
              const vinculadoNaArvore =
                filhoPorId.get(linha.linkedWorkItemId) ??
                byId.get(linha.linkedWorkItemId) ??
                (filtroAtivo ? undefined : completos.get(linha.linkedWorkItemId))
              if (vinculadoNaArvore && !abaixo.includes(vinculadoNaArvore.id)) {
                // o filho mora no checklist: fechado ou não, não sai de novo lá embaixo
                if (filhoPorId.has(vinculadoNaArvore.id)) noChecklist.add(vinculadoNaArvore.id)
                /*
                  A chave leva a LINHA do checklist (SPEC-20261022-1012-arvore-chave-unica):
                  o mesmo item ligado em dois checklists do mesmo pai saía com
                  a mesma chave nas duas vezes, e o React, com chave repetida,
                  desenhava linhas fora do lugar e deixava linhas de quem já
                  estava recolhido (WI-0628 dentro do WI-0651, 2026-10-22).
                */
                if (!fechado) walk([vinculadoNaArvore], depth + 2, row.raiz, abaixo, `${prefixoAbaixo}ln:${linha.id}>`)
                continue
              }
              // com filtro, o item que não passou some
              if (filtroAtivo) continue
              // item que nem veio para a tela (de quem não se vê): a linha aponta para ele
              if (!fechado) {
                const vinculado = linha.vinculado ?? nomePorId.get(linha.linkedWorkItemId) ?? null
                todos.push({
                  tipo: 'linha',
                  id: `${prefixoAbaixo}ln:${linha.id}`,
                  linhaId: linha.id,
                  texto: vinculado?.title ?? linha.text,
                  feito: linhaDeChecklistFeita(linha, concluidoPorId.get(linha.linkedWorkItemId)),
                  depth: depth + 2,
                  raiz: row.raiz,
                  vinculado,
                })
              }
              continue
            }
            // linha solta: com filtro, só no item que passou (é o conteúdo dele)
            if (!fechado && !(filtroAtivo && row.contexto)) {
              todos.push({ tipo: 'linha', id: `${prefixoAbaixo}ln:${linha.id}`, linhaId: linha.id, texto: linha.text, feito: linha.isDone, depth: depth + 2, raiz: row.raiz, vinculado: null })
            }
          }
        }
        walk(row.children.filter((c) => !noChecklist.has(c.id)), depth + 1, row.raiz, abaixo, prefixoAbaixo)
      }
    }
    walk(roots, 0, null, [], '')
    return { tree: flat, nos: todos, linhasDaArvore: [...completos.values()] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, universo, ancestrais, collapsedNodes, comparador])

  /**
   * ARQUIVADO À VISTA (SPEC-20261008-1639-item-arquivado-visivel, pedido do Cristian: "colocar algo
   * visual que possa ser identificado que o item está arquivado e que dentro
   * tem itens não arquivados junto"): para cada pai arquivado, quantos itens
   * ATIVOS da lista estão debaixo dele, em qualquer nível.
   */
  const ativosDentroDoArquivado = useMemo(() => {
    const arquivados = new Set(ancestrais.filter((a) => a.arquivado).map((a) => a.id))
    const contagem = new Map<string, number>()
    if (arquivados.size === 0) return contagem
    const paiDe = new Map([...ancestrais, ...allItems].map((i) => [i.id, i.parentWorkItemId]))
    for (const item of allItems) {
      const subindo = new Set<string>([item.id])
      let acima = item.parentWorkItemId
      while (acima && !subindo.has(acima)) {
        subindo.add(acima)
        if (arquivados.has(acima)) contagem.set(acima, (contagem.get(acima) ?? 0) + 1)
        acima = paiDe.get(acima) ?? null
      }
    }
    return contagem
  }, [ancestrais, allItems])

  /** Por que um pai está ali só de contexto — vira a dica da linha. */
  const motivoDoContexto = useMemo(() => {
    const motivos = new Map<string, string>()
    for (const a of ancestrais) {
      motivos.set(
        a.id,
        a.oculto
          ? 'Item privado — aparece para manter a hierarquia'
          : a.arquivado
            ? 'Arquivado — aparece para manter a hierarquia'
            : 'Fora desta lista — aparece para manter a hierarquia',
      )
    }
    return motivos
  }, [ancestrais])

  /** A visão em lista usa a mesma ordem da árvore, sem hierarquia a preservar. */
  const itensOrdenados = useMemo(() => [...items].sort(comparador), [items, comparador])

  /**
   * Os dois grupos da tabela (SPEC-20261002-1241).
   *
   * AVULSO é o item que NÃO TEM PAI (SPEC-20261008-1226-arvore-recursiva-e-avulsos: "conceito de itens
   * avulsos é aquele item que não possui pai") e nada dentro dele. Item com
   * pai nunca é avulso: fica dentro da árvore de quem está em cima dele —
   * mesmo com o pai fora do filtro (aí o pai aparece como contexto) ou
   * invisível para quem vê (aí ele abre a lista principal, não os avulsos).
   * Um item sem pai MAS com filhos ou checklists é quem abre a árvore.
   *
   * Eles vão para uma faixa no fim, e não para outra aba: separá-los em outra
   * visão esconderia justamente o que se quer mostrar — que existem itens
   * soltos.
   */
  const { principais, avulsos } = useMemo(() => {
    const temFilho = new Set<string>()
    for (const row of items) if (row.parentWorkItemId) temFilho.add(row.parentWorkItemId)
    // item com checklist também tem estrutura: não é avulso
    const soltos = new Set(
      tree
        .filter(
          (r) => r.depth === 0 && !r.parentWorkItemId && r.children.length === 0 && !temFilho.has(r.id) && checklistsDe(r).length === 0,
        )
        .map((r) => r.id),
    )
    // checklists e linhas vão no grupo do item de onde a árvore saiu
    return {
      principais: nos.filter((n) => !soltos.has(n.raiz)),
      avulsos: nos.filter((n) => soltos.has(n.raiz)),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, nos, items])

  /**
   * As linhas da LINHA DO TEMPO: as mesmas da tabela (árvore com checklists e
   * avulsos, ou a lista plana), no formato do componente (SPEC-20261002-1241-linha-do-tempo-de-itens).
   */
  const statusIniciais = useMemo(
    () => new Set((bootstrap.data?.workItemStatuses ?? []).filter((s) => s.isInitial).map((s) => s.id)),
    [bootstrap.data],
  )
  const linhasDoTempo = useMemo((): LinhaDoTempoLinha[] => {
    if (!linhaDoTempo) return []
    const doItem = (row: (typeof tree)[number], comArvore: boolean): LinhaDoTempoLinha => {
      const prog = progressoDe(row.id)
      return {
        tipo: 'item',
        chave: row.chave,
        depth: row.depth,
        id: row.id,
        code: row.code,
        title: row.title,
        startAt: row.startAt,
        dueAt: row.dueAt,
        status: row.status,
        temFilhos: comArvore && (row.children.length > 0 || checklistsDe(row).length > 0),
        recolhido: collapsedNodes.has(row.id),
        contexto: row.contexto,
        progresso: { feitos: prog.feitos, total: prog.total },
      }
    }
    if (view !== 'tree') {
      return itensOrdenados.map((row) =>
        doItem({ ...row, children: [], depth: 0, raiz: row.id, tipo: 'item' as const, chave: row.id, contexto: false }, false),
      )
    }
    const doNo = (no: (typeof nos)[number]): LinhaDoTempoLinha => {
      if (no.tipo === 'item') return doItem(no, true)
      if (no.tipo === 'checklist') {
        return {
          tipo: 'grupo',
          chave: no.chaveDeRecolher,
          depth: no.depth,
          titulo: no.nome,
          detalhe: `${no.feitos}/${no.total}`,
          recolhido: collapsedNodes.has(no.chaveDeRecolher),
        }
      }
      return {
        tipo: 'texto',
        chave: no.id,
        depth: no.depth,
        titulo: no.vinculado ? `${no.vinculado.code} ${no.vinculado.title}` : no.texto,
        feito: no.feito,
      }
    }
    return [
      ...principais.map(doNo),
      ...(avulsos.length > 0
        ? [{ tipo: 'grupo' as const, chave: 'grupo:avulsos', depth: 0, titulo: 'Itens avulsos', detalhe: `${avulsos.length} sem item pai` }]
        : []),
      ...avulsos.map(doNo),
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhaDoTempo, view, principais, avulsos, itensOrdenados, collapsedNodes, progressoDe])


  /** Ids que têm filhos — a base do "expandir/recolher tudo". */
  const idsComFilhos = useMemo(() => {
    const pais = new Set<string>()
    // toda a árvore visível (inclui o contexto), não só o que passou no filtro
    for (const row of linhasDaArvore) {
      if (row.children.length > 0) pais.add(row.id)
      const checklists = checklistsDe(row)
      if (checklists.length > 0) pais.add(row.id)
      for (const c of checklists) pais.add(chaveDoChecklist(c.id))
    }
    return [...pais]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhasDaArvore])
  /** Algum nó ainda aberto? Define se o botão recolhe ou expande. */
  const algumNoAberto = idsComFilhos.some((id) => !collapsedNodes.has(id))

  const dueLabel = (dueAt: string | null, isTerminal: boolean) => {
    if (!dueAt || isTerminal) return null
    const days = Math.ceil((new Date(dueAt).getTime() - Date.now()) / 86_400_000)
    const text = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(new Date(dueAt)).replace('.', '')
    const color = days < 0 ? 'var(--ob-danger)' : days <= 3 ? 'var(--ob-warning-deep)' : 'var(--ob-text-muted)'
    return (
      <span className="wi-row__due" style={{ color, fontWeight: days < 0 ? 600 : 400 }}>
        {days < 0 ? `${text} · atrasado` : text}
      </span>
    )
  }

  /**
   * Uma linha da tabela (SPEC-20261002-1241).
   *
   * A linha inteira é o alvo de seleção, e a indentação da árvore vive só na
   * primeira célula — por isso `grid` e não `<table>`: para indentar por
   * profundidade dentro de uma célula de tabela seria preciso `display:
   * contents` no `<tr>`, que quebra a borda e o fundo da linha.
   */
  /** O conteúdo de uma célula, pela coluna. */
  const celulaDaColuna = (
    key: ColunaKey,
    row: (typeof tree)[number],
    prog: { feitos: number; total: number },
  ) => {
    switch (key) {
      case 'status':
        return (
          <span>
            <StatusPill tone={row.status.tone}>{catalogLabel(row.status)}</StatusPill>
          </span>
        )
      case 'responsavel':
        return (
          <span className="wi-table__pessoa">
            {row.assignee ? (
              <>
                <span className="wi-avatar" title={row.assignee.name}>
                  {initialsOf(row.assignee.name)}
                </span>
                <span className="wi-table__texto">{row.assignee.name}</span>
              </>
            ) : (
              <span className="wi-table__vazio">
                <span className="wi-table__semfoto" aria-hidden />
                Atribuir
              </span>
            )}
          </span>
        )
      case 'prazo':
        return (
          <span className="wi-table__prazo">
            {dueLabel(row.dueAt, row.status.isTerminal) ?? <span className="wi-table__vazio">—</span>}
          </span>
        )
      case 'progresso':
        return (
          <span className="wi-table__progresso">
            {/*
              Mostra a barra quando há o que medir — e não só quando a linha tem
              filho na árvore: o item cujo trabalho está em checklists (linhas
              soltas, itens vinculados de outro lugar) também tem progresso.
            */}
            {prog.total > 0 ? (
              <>
                <span
                  className="wi-table__barra"
                  title={`${prog.feitos} de ${prog.total} concluídos`}
                  aria-label={`${prog.feitos} de ${prog.total} concluídos`}
                >
                  <span
                    className="wi-table__barra-fill"
                    style={{ width: `${prog.total === 0 ? 0 : Math.round((prog.feitos / prog.total) * 100)}%` }}
                  />
                </span>
                <em>{`${prog.feitos}/${prog.total}`}</em>
              </>
            ) : (
              <span className="wi-table__vazio">—</span>
            )}
          </span>
        )
      case 'prioridade':
        return (
          <span className="wi-table__texto">
            {row.priority ? catalogLabel(row.priority) : <span className="wi-table__vazio">—</span>}
          </span>
        )
      case 'etapa':
        return (
          <span className="wi-table__texto" title={row.stage ? `${row.stage.flow} · ${row.stage.step}` : undefined}>
            {row.stage?.step ?? <span className="wi-table__vazio">—</span>}
          </span>
        )
      case 'tipo':
      case 'empresa':
      case 'area':
      case 'portfolio':
      case 'conclusao':
      case 'criado':
      case 'atualizado':
        return (
          <span className="wi-table__texto" title={textoSimples(row, key) ?? undefined}>
            {textoSimples(row, key) ?? <span className="wi-table__vazio">—</span>}
          </span>
        )
      case 'inicio':
        return (
          <span className="wi-table__prazo">
            {row.startAt ? (
              new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' })
                .format(new Date(row.startAt))
                .replace('.', '')
            ) : (
              <span className="wi-table__vazio">—</span>
            )}
          </span>
        )
      default: {
        // coluna da TRILHA: o checkbox de etapa ou de status
        const marca = marcaDaTrilha(row, key)
        if (marca) {
          const ocupado = marcarTrilha.isPending && marcarTrilha.variables?.itemId === row.id
          return (
            <span className="wi-table__trilha" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                className="wi-table__marca"
                checked={marca.marcado}
                disabled={!marca.habilitado || ocupado}
                aria-label={`${rotuloDaColuna(key, catalogo)} — ${marca.motivo}`}
                title={marca.motivo}
                onChange={() =>
                  void pedirNaTrilha(row, {
                    itemId: row.id,
                    codigo: row.code,
                    alvo: marca.alvo,
                    id: marca.id,
                    marcar: !marca.marcado,
                  })
                }
              />
            </span>
          )
        }
        // campo complementar: a célula lê de `customValues`
        return (
          <span className="wi-table__texto" title={textoSimples(row, key) ?? undefined}>
            {textoSimples(row, key) ?? <span className="wi-table__vazio">—</span>}
          </span>
        )
      }
    }
  }

  const linhaDaTabela = (row: (typeof tree)[number]) => {
    // na visão em lista não há árvore: o checklist só abre na árvore
    const temFilhos = row.children.length > 0 || (view === 'tree' && checklistsDe(row).length > 0)
    const recolhido = collapsedNodes.has(row.id)
    const prog = progressoDe(row.id)
    const selecionada = row.id === (selectedId ?? resumoId)
    return (
      <div
        key={row.chave}
        role="row"
        className={`wi-table__row${selecionada ? ' wi-table__row--on' : ''}${temFilhos ? ' wi-table__row--pai' : ''}${row.contexto ? ' wi-table__row--contexto' : ''}`}
        title={
          row.contexto ? (motivoDoContexto.get(row.id) ?? 'Fora do filtro — aparece para manter a hierarquia') : undefined
        }
        onClick={() => {
          // a linha ABRE O RESUMO; quem leva para a edição é a chave
          setResumoId(row.id)
                  }}
      >
        <span className="wi-table__titulo" style={{ paddingLeft: row.depth * 20 }}>
          <button
            type="button"
            className="wi-table__caret"
            aria-label={recolhido ? `Expandir ${row.title}` : `Recolher ${row.title}`}
            aria-expanded={temFilhos ? !recolhido : undefined}
            style={{ visibility: temFilhos ? 'visible' : 'hidden' }}
            onClick={(e) => {
              e.stopPropagation()
              alternarRecolhido(row.id)
            }}
          >
            <i className={`fas fa-chevron-${recolhido ? 'right' : 'down'}`} aria-hidden />
          </button>
          <span className="wi-table__tipo" title={catalogLabel(row.type)}>
            <i className={row.type.icon ?? 'fas fa-tasks'} aria-hidden />
          </span>
          {/*
            O CÓDIGO vem antes do título, na mesma célula (pedido do Cristian,
            2026-10-02), e deixou de ser coluna: como coluna ele empurrava o
            título para longe do olho e gastava largura fixa numa informação
            curta. Continua sendo o link para a edição.
          */}
          <Link
            to={rotaDoItem(row.id)}
            className="wi-table__chave wi-table__chave--link"
            title={`Abrir ${row.code} para editar`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.code}
          </Link>
          <span className="wi-table__texto">{row.title}</span>
          {/* arquivado: selo com ícone E texto (estado nunca só por cor), e
              quantos itens ativos continuam ali dentro */}
          {ativosDentroDoArquivado.has(row.id) || motivoDoContexto.get(row.id)?.startsWith('Arquivado') ? (
            <span className="wi-arquivado-selo" title="Este item está arquivado: não aparece nas listas e quadros">
              <i className="fas fa-archive" aria-hidden /> Arquivado
            </span>
          ) : null}
          {(ativosDentroDoArquivado.get(row.id) ?? 0) > 0 ? (
            <em className="wi-arquivado-ativos" title="Itens ativos guardados dentro deste item arquivado">
              {`${ativosDentroDoArquivado.get(row.id)} ${ativosDentroDoArquivado.get(row.id) === 1 ? 'ativo' : 'ativos'} dentro`}
            </em>
          ) : null}
          {/* recolhido, o pai diz quanta coisa está escondida ali dentro */}
          {temFilhos && recolhido ? (
            <em className="wi-table__filhos">{`${prog.total} ${prog.total === 1 ? 'item' : 'itens'}`}</em>
          ) : null}
        </span>

        {/*
          A CHAVE é o caminho para EDITAR (SPEC-20261002-1541). Link de verdade,
          com href, e não um span com onClick: assim valem o "abrir em nova aba"
          e o teclado, que é o que se espera de algo com cara de link.

          O clique PARA aqui: sem isso o mesmo toque abriria o painel de resumo
          e navegaria, e a tela piscaria entre os dois destinos.
        */}
        {/*
          As células saem na ORDEM escolhida, e só as ligadas. Uma por caso,
          num lugar só: espalhar o condicional por quatro blocos foi o que fez a
          grade e as células divergirem quando a coluna mudava.
        */}
        {visiveis.map((c) => (
          <Fragment key={c.k}>{celulaDaColuna(c.k, row, prog)}</Fragment>
        ))}
      </div>
    )
  }

  /** Abre ou fecha um nó da árvore (item ou checklist). */
  const alternarNo = (chave: string) => alternarRecolhido(chave)

  /** Marcar a linha solta do checklist direto na lista (SPEC-20261007-2212-checklist-na-lista-de-itens). */
  const marcarLinha = useMutation({
    mutationFn: async (input: { linhaId: string; feito: boolean }) =>
      api['work-items']['checklist-items'][':itemId'].$patch({
        param: { itemId: input.linhaId },
        json: { isDone: input.feito },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-items'] }),
    onError: (e) =>
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível marcar a linha.' }),
  })

  /** As células das demais colunas, vazias: checklist e linha só têm o título. */
  const celulasVazias = () => visiveis.map((c) => <span key={c.k} aria-hidden />)

  const noDaTabela = (no: (typeof nos)[number]) => {
    if (no.tipo === 'item') return linhaDaTabela(no)
    if (no.tipo === 'checklist') {
      const fechado = collapsedNodes.has(no.chaveDeRecolher)
      return (
        <div key={no.id} role="row" className="wi-table__row wi-table__row--checklist">
          <span className="wi-table__titulo" style={{ paddingLeft: no.depth * 20 }}>
            <button
              type="button"
              className="wi-table__caret"
              aria-label={fechado ? `Expandir o checklist ${no.nome}` : `Recolher o checklist ${no.nome}`}
              aria-expanded={!fechado}
              onClick={() => alternarNo(no.chaveDeRecolher)}
            >
              <i className={`fas fa-chevron-${fechado ? 'right' : 'down'}`} aria-hidden />
            </button>
            <span className="wi-table__tipo" aria-hidden>
              <i className="fas fa-tasks" />
            </span>
            <span className="wi-table__texto wi-table__checklist-nome">{no.nome}</span>
            <em className="wi-table__filhos">{`${no.feitos}/${no.total}`}</em>
          </span>
          {celulasVazias()}
        </div>
      )
    }
    if (no.vinculado) {
      /*
        Linha que aponta para um item de OUTRO lugar da árvore. O "feito" é o
        status do item — marcar aqui gravaria na linha uma marca que ninguém
        lê —, então o checkbox só mostra; o código leva ao item, para mudar o
        status lá.
      */
      const situacao = no.feito ? 'concluído' : 'em aberto'
      return (
        <div key={no.id} role="row" className={`wi-table__row wi-table__row--linha${no.feito ? ' wi-table__row--feita' : ''}`}>
          <span className="wi-table__titulo wi-table__linha-check" style={{ paddingLeft: no.depth * 20 + 22 }}>
            <input
              type="checkbox"
              className="wi-table__marca"
              checked={no.feito}
              disabled
              readOnly
              aria-label={`${no.vinculado.code} ${situacao} — segue o status do Item de Trabalho`}
              title={`${situacao[0]!.toUpperCase()}${situacao.slice(1)} — segue o status do Item de Trabalho`}
            />
            <Link
              to={rotaDoItem(no.vinculado.id)}
              className="wi-table__chave wi-table__chave--link"
              title={`Abrir ${no.vinculado.code} para editar`}
            >
              {no.vinculado.code}
            </Link>
            <span className="wi-table__texto">{no.texto}</span>
          </span>
          {celulasVazias()}
        </div>
      )
    }
    const ocupada = marcarLinha.isPending && marcarLinha.variables?.linhaId === no.linhaId
    return (
      <div key={no.id} role="row" className={`wi-table__row wi-table__row--linha${no.feito ? ' wi-table__row--feita' : ''}`}>
        <label className="wi-table__titulo wi-table__linha-check" style={{ paddingLeft: no.depth * 20 + 22 }}>
          <input
            type="checkbox"
            className="wi-table__marca"
            checked={no.feito}
            disabled={ocupada}
            onChange={() => marcarLinha.mutate({ linhaId: no.linhaId, feito: !no.feito })}
          />
          <span className="wi-table__texto">{no.texto || <span className="wi-table__vazio">Sem texto</span>}</span>
        </label>
        {celulasVazias()}
      </div>
    )
  }

  return (
    <div className="ob-root wi-split">
      {/*
        Com um item aberto, o explorer CONTINUA (pedido do Cristian,
        2026-10-03): a edição usa a área à direita dele. Antes a lista sumia
        (SPEC-20261002-1541), e quem editava perdia de vista onde estava.

        Quem quiser a tela inteira recolhe o explorer pela seta, como em
        qualquer outra tela.
      */}
      {explorerCollapsed && !compacto ? (
        <div className="wi-resizer" style={{ cursor: 'default' }}>
          <button
            type="button"
            className="wi-resizer__toggle"
            aria-label="Mostrar explorer"
            title="Mostrar explorer"
            onClick={() => setExplorerCollapsed(false)}
          >
            <i className="fas fa-chevron-right" aria-hidden style={{ fontSize: 9 }} />
          </button>
        </div>
      ) : (
      <aside ref={explorerRef} className="wi-explorer wi-explorer--principal">
        {embutido ? null : <WorkspaceBar />}
        {/*
          O cabeçalho (título, busca, filtro, colunas, visão, novo) pode ser
          desenhado FORA do painel da lista, sobre o fundo, como o do quadro
          (pedido do Cristian, 2026-10-06). É um portal: o estado continua
          aqui, e a busca, o filtro e as colunas funcionam como antes.
        */}
        <NoSlot alvo={slotDoCabecalho}>
        {/*
          Fora do painel, o cabeçalho é montado com a MESMA ESTRUTURA do
          cabeçalho do quadro — `kb__head` > `kb__titlerow` (título, busca,
          filtro) + `kb__actions` — e não com um visual imitado: as medidas e
          o respiro até o painel saem das mesmas regras, e continuam iguais se o
          quadro mudar (pedido do Cristian, 2026-10-06). Dentro do painel os
          grupos somem (`display: contents`) e vale o desenho de sempre.
        */}
        <div className={slotDoCabecalho ? 'kb__head' : 'wi-explorer__head'}>
          <div className={slotDoCabecalho ? 'kb__titlerow' : 'wi-explorer__grupo'}>
          <h1
            className={
              slotDoCabecalho
                ? 'kb__title'
                : `wi-explorer__title${controlesDoQuadro ? '' : ' wi-explorer__title--com-busca'}`
            }
          >
            Itens de Trabalho
          </h1>
          {/*
            Busca ao lado do TÍTULO e filtro ao lado da busca — o mesmo desenho
            do cabeçalho do quadro (pedido do Cristian, 2026-10-06). Dentro de
            um quadro elas nem aparecem: valem as do cabeçalho dele. Aqui, no nó
            de Itens, não há quadro, e estas são as únicas.
          */}
          {controlesDoQuadro ? null : (
            <div className={slotDoCabecalho ? 'wi-explorer__grupo' : 'wi-explorer__headbusca'}>
              {slotDoCabecalho ? (
                /*
                  A MESMA busca do cabeçalho do quadro — marcação e classe
                  (`kb__buscar`), e não um visual imitado: altura, largura,
                  contagem dos achados e o X para limpar ficam iguais nos dois,
                  e continuam iguais se o quadro mudar.
                */
                <div className={`kb__buscar${search.trim() ? ' kb__buscar--ativa' : ''}`}>
                  <i className="fas fa-search" aria-hidden />
                  <input
                    value={search}
                    placeholder="Localizar item…"
                    aria-label="Buscar Itens de Trabalho"
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation()
                        setSearch('')
                      }
                    }}
                  />
                  {search.trim() ? (
                    <>
                      <span className="kb__buscar-n" title="Itens encontrados">
                        {items.length}
                      </span>
                      <button
                        type="button"
                        className="kb__buscar-x"
                        aria-label="Limpar a busca"
                        title="Limpar a busca (Esc)"
                        onClick={() => setSearch('')}
                      >
                        <i className="fas fa-times" aria-hidden />
                      </button>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="wi-explorer__searchbox">
                  <i className="fas fa-search" aria-hidden />
                  <input
                    className="wi-explorer__search"
                    type="search"
                    placeholder="Localizar item…"
                    aria-label="Buscar Itens de Trabalho"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              )}

              {/* o invólucro do filtro do quadro: o mesmo respiro depois da busca */}
              <div className={slotDoCabecalho ? 'ob-filterbar ob-filterbar--combo kb__cardfilters' : 'wi-explorer__grupo'}>
                <FiltroDaLista
                  colunas={{ catalogo, atuais: colunasLigadas(colunas) }}
                  modoAtual={linhaDoTempo ? 'tempo' : 'lista'}
                  ordenacaoAtual={ordenacao}
                  selecao={selecao}
                  onSelecionar={setSelecao}
                  statusOptions={statusOptions}
                  typeOptions={typeOptions}
                  priorityOptions={priorityOptions}
                  memberOptions={memberOptions}
                  loadValueOptions={opcoesDoFiltro}
                  campos={camposDoFiltro}
                />
              </div>
            </div>
          )}
          </div>
          <div className={slotDoCabecalho ? 'kb__actions' : 'wi-explorer__grupo'}>
          {/*
            COLUNAS DA LISTA (pedido do Cristian, 2026-10-02). Fica ao lado da
            troca de visão porque as duas respondem à mesma pergunta: como eu
            quero ver esta lista.
          */}
          <div className="wi-colunas">
            <button
              type="button"
              className={`wi-colunas__botao${menuColunas ? ' wi-colunas__botao--on' : ''}`}
              onClick={() => setMenuColunas((v) => !v)}
              aria-expanded={menuColunas}
              aria-haspopup="true"
              aria-label="Colunas da lista"
              title="Colunas da lista"
            >
              {/* Três colunas numa moldura arredondada (o desenho pedido pelo
                  Cristian, 2026-10-06). Em SVG porque o FontAwesome 5.15 que o
                  app carrega não tem esse ícone — o fa-columns só tem duas, e
                  o fa-table-columns é do 6 e deixava o botão em branco. */}
              <svg className="wi-colunas__icone" viewBox="0 0 16 16" aria-hidden>
                <rect x="1.5" y="3" width="13" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M6 3v10M10 3v10" fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>

            {menuColunas ? (
              <>
                {/* fecha ao clicar em qualquer lugar fora, sem roubar o clique
                    de quem mira direto outro botão da barra */}
                <div className="wi-colunas__fora" onClick={() => setMenuColunas(false)} aria-hidden />
                <div className="wi-colunas__menu" role="menu">
                  <div className="wi-colunas__topo">
                    <strong>Colunas da lista</strong>
                    <button type="button" className="rs__link" onClick={() => setTabela(padraoDaVez)}>
                      Restaurar
                    </button>
                  </div>

                  {/* o título não sai: é onde mora o código e o que identifica a linha */}
                  <label className="wi-colunas__item wi-colunas__item--fixa">
                    <input type="checkbox" checked disabled />
                    <span>Título</span>
                    <em>fixa</em>
                  </label>

                  {/*
                    Busca pelo NOME da coluna: com os campos complementares do
                    workspace, a lista passa de oito para quantos cada empresa
                    tiver cadastrado, e rolar atrás de uma deixa de servir.
                  */}
                  <div className="wi-colunas__busca">
                    <i className="fas fa-search" aria-hidden />
                    <input
                      type="search"
                      value={buscaColuna}
                      onChange={(e) => setBuscaColuna(e.target.value)}
                      placeholder="Buscar coluna"
                      aria-label="Buscar coluna pelo nome"
                    />
                  </div>

                  {/* o Título está fora da lista: é a coluna fixa logo acima */}
                  {colunasDoMenu.map((c, i) => (
                    <label key={c.k} className="wi-colunas__item">
                      <input
                        type="checkbox"
                        checked={c.on}
                        onChange={() => alternarColuna(c.k)}
                      />
                      <span>{rotuloDaColuna(c.k, catalogo)}</span>
                      <button
                        type="button"
                        className="wi-colunas__mover"
                        aria-label={`Subir ${rotuloDaColuna(c.k, catalogo)}`}
                        disabled={i === 0 || buscaColuna.trim() !== ''}
                        onClick={(e) => {
                          e.preventDefault()
                          moverColuna(c.k, -1)
                        }}
                      >
                        <i className="fas fa-chevron-up" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="wi-colunas__mover"
                        aria-label={`Descer ${rotuloDaColuna(c.k, catalogo)}`}
                        /* com busca, a posição na tela não é a posição real:
                           mover ali trocaria de lugar com quem nem aparece */
                        disabled={i === colunas.length - 2 || buscaColuna.trim() !== ''}
                        onClick={(e) => {
                          e.preventDefault()
                          moverColuna(c.k, 1)
                        }}
                      >
                        <i className="fas fa-chevron-down" aria-hidden />
                      </button>
                    </label>
                  ))}

                  {colunasDoMenu.length === 0 ? (
                    <p className="wi-colunas__nota">Nenhuma coluna com esse nome.</p>
                  ) : null}

                  <p className="wi-colunas__nota">
                    Guardado na sua conta: vale em qualquer computador. Arraste a borda do
                    cabeçalho para mudar a largura, e o cabeçalho para mudar de lugar.
                  </p>
                </div>
              </>
            ) : null}
          </div>

          <div className="wi-explorer__viewtoggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'list'}
              aria-label="Ver em lista"
              title="Lista"
              className={`wi-explorer__viewbtn${view === 'list' ? ' wi-explorer__viewbtn--on' : ''}`}
              onClick={() => setView('list')}
            >
              <i className="fas fa-list" aria-hidden style={{ fontSize: 12 }} />
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'tree'}
              aria-label="Ver em árvore"
              title="Árvore"
              className={`wi-explorer__viewbtn${view === 'tree' ? ' wi-explorer__viewbtn--on' : ''}`}
              onClick={() => setView('tree')}
            >
              <i className="fas fa-sitemap" aria-hidden style={{ fontSize: 12 }} />
            </button>
          </div>
          {/* Tabela | Linha do tempo (SPEC-20261002-1241-linha-do-tempo-de-itens, alternador do protótipo);
              com o modo fixo (quadro), quem escolhe é o seletor do quadro */}
          {modo ? null : (
          <div className="wi-explorer__viewtoggle" role="tablist" aria-label="Modo da lista">
            <button
              type="button"
              role="tab"
              aria-selected={!linhaDoTempo}
              aria-label="Ver em tabela"
              title="Tabela"
              className={`wi-explorer__viewbtn${!linhaDoTempo ? ' wi-explorer__viewbtn--on' : ''}`}
              onClick={() => trocarModoDaLista('tabela')}
            >
              <i className="fas fa-table" aria-hidden style={{ fontSize: 12 }} />
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={linhaDoTempo}
              aria-label="Ver na linha do tempo"
              title="Linha do tempo"
              className={`wi-explorer__viewbtn${linhaDoTempo ? ' wi-explorer__viewbtn--on' : ''}`}
              onClick={() => trocarModoDaLista('tempo')}
            >
              <i className="fas fa-stream" aria-hidden style={{ fontSize: 12 }} />
            </button>
          </div>
          )}
          {podeCriar ? (
            <button
              type="button"
              className="wi-explorer__create"
              aria-label="Criar"
              title="Novo Item de Trabalho"
              disabled={criandoItem || !bootstrap.data}
              // Com templates na biblioteca, o "+" pergunta antes: template ou
              // em branco (SPEC-20260830-1225). Sem nenhum, cria direto.
              onClick={() => (temTemplates ? setEscolherTemplate(true) : void criarEAbrir())}
            >
              <i className={`fas ${criandoItem ? 'fa-spinner fa-spin' : 'fa-plus'}`} aria-hidden style={{ fontSize: 12 }} />
            </button>
          ) : null}
          </div>
        </div>
        </NoSlot>

        <div
          ref={tabelaRef}
          className="wi-explorer__list wi-explorer__list--tabela"
          // segurar e arrastar nas linhas rola a tabela (a linha do tempo tem a dela)
          onPointerDown={linhaDoTempo ? undefined : (e) => iniciarArrasteDeRolagem(e, tabelaRef.current)}
        >
          {list.isPending ? (
            <div style={{ padding: 14 }}>
              <LoadingState rows={7} />
            </div>
          ) : linhaDoTempo ? (
            <LinhaDoTempo
              linhas={linhasDoTempo}
              statusIniciais={statusIniciais}
              selecionadoId={selectedId ?? resumoId}
              onSelecionar={(id) => setResumoId(id)}
              onAlternar={(chave) => alternarRecolhido(chave)}
            />
          ) : (
            <div
              className="wi-table"
              role="table"
              aria-label="Itens de Trabalho"
              /* a grade é montada a partir das colunas ligadas: coluna escondida
                 não pode deixar uma faixa vazia no lugar dela */
              style={{
                /* uma faixa por coluna ligada, na largura que a pessoa deixou,
                   mais uma faixa livre no fim para a linha chegar à borda
                   quando sobra espaço */
                ['--wi-cols' as string]: trilhosDaGrade(
                  ligadas.map((c) => ({ ...c, w: larguraDaColuna(c.k) })),
                ),
              }}
            >
              <div className="wi-table__head" role="row" ref={cabecaRef}>
                {ligadas.map((c) => (
                  <span
                    key={c.k}
                    role="columnheader"
                    aria-sort={
                      ordenacao?.k === c.k
                        ? ordenacao.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                    className={`wi-table__th${arrastada === c.k ? ' wi-table__th--indo' : ''}${alvoSolta === c.k ? ' wi-table__th--alvo' : ''}`}
                    data-coluna={c.k}
                    /* o Título é a âncora da linha: não se arrasta nem recebe
                       outra coluna na frente dele */
                    onPointerDown={c.k === 'titulo' ? undefined : pegarColuna(c.k)}
                  >
                    <button
                      type="button"
                      className="wi-table__ordenar"
                      onClick={() => alternarOrdem(c.k)}
                      title={`Ordenar por ${rotuloDaColuna(c.k, catalogo)}`}
                    >
                      <span className="wi-table__thtxt">{rotuloDaColuna(c.k, catalogo)}</span>
                      {/* a seta só aparece na coluna ordenada: uma seta apagada
                          em cada cabeçalho vira ruído e nenhuma informação */}
                      {ordenacao?.k === c.k ? (
                        <i
                          className={`fas fa-arrow-${ordenacao.dir === 'asc' ? 'up' : 'down'} wi-table__seta`}
                          aria-hidden
                        />
                      ) : null}
                    </button>
                    {/*
                      Expandir/recolher a árvore no cabeçalho da coluna Título,
                      encostado à direita dela (pedido do Cristian, 2026-10-06):
                      é a coluna da árvore, e o botão age sobre ela. Antes ele
                      ocupava uma linha inteira só para si, entre o cabeçalho
                      da tela e a tabela.
                    */}
                    {c.k === 'titulo' && view === 'tree' ? (
                      <button
                        type="button"
                        className="wi-explorer__tudo wi-table__tudo"
                        aria-label={algumNoAberto ? 'Recolher tudo' : 'Expandir tudo'}
                        title={algumNoAberto ? 'Recolher tudo' : 'Expandir tudo'}
                        onClick={() => setArvoreGuardada(tudoNaArvore(algumNoAberto ? 'fechado' : 'aberto'))}
                      >
                        <i className={`fas fa-${algumNoAberto ? 'compress-alt' : 'expand-alt'}`} aria-hidden />
                      </button>
                    ) : null}
                    <span
                      className={`wi-table__puxador${arrastando?.k === c.k ? ' wi-table__puxador--on' : ''}`}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Largura da coluna ${rotuloDaColuna(c.k, catalogo)}`}
                      onPointerDown={iniciarArraste(c.k)}
                      onPointerMove={seguirArraste}
                      onPointerUp={soltarArraste}
                      onPointerCancel={soltarArraste}
                    />
                  </span>
                ))}
              </div>

              {/*
                A LISTA É A MESMA nas duas visões (pedido do Cristian,
                2026-10-02). O que muda é só o agrupamento: em árvore os itens
                saem debaixo do pai mais alto, com a faixa dos avulsos no fim;
                em lista saem todos no mesmo nível, sem indentação.

                Duas tabelas diferentes seria o defeito que ele apontou — uma
                opção mostrando colunas e a outra, cartões.
              */}
              {view === 'tree' ? (
                <>
                  {principais.map((no) => noDaTabela(no))}
                  {avulsos.length > 0 ? (
                    <div className="wi-table__grupo" role="row">
                      <span>
                        <i className="fas fa-circle-notch" aria-hidden /> Itens avulsos
                      </span>
                      <em>{`${avulsos.length} sem item pai`}</em>
                    </div>
                  ) : null}
                  {avulsos.map((no) => noDaTabela(no))}
                </>
              ) : (
                itensOrdenados.map((row) =>
                  linhaDaTabela({ ...row, children: [], depth: 0, raiz: row.id, tipo: 'item' as const, chave: row.id, contexto: false }),
                )
              )}
            </div>
          )}

          {list.data && items.length === 0 ? (
            <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12.5, color: 'var(--ob-text-muted)', textAlign: 'center', padding: '24px 12px' }}>
              Ainda não há Itens de Trabalho neste contexto.
            </p>
          ) : null}
        </div>
      </aside>
      )}

      {!explorerCollapsed ? (
        <div
          className="wi-resizer"
          title="Arraste para redimensionar · use a seta para recolher"
          onMouseDown={() => {
            resizing.current = true
            document.body.style.userSelect = 'none'
          }}
        >
          <button
            type="button"
            className="wi-resizer__toggle"
            aria-label="Recolher explorer"
            title="Recolher explorer"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setExplorerCollapsed(true)}
          >
            <i className="fas fa-chevron-left" aria-hidden style={{ fontSize: 9 }} />
          </button>
        </div>
      ) : null}

      {selectedId !== null || resumoId !== null ? (
        <div
          ref={resumoRef}
          className={selectedId === null ? 'wi-resumo wi-resumo--entra' : 'wi-resumo wi-resumo--cheio'}
          /* so o PAINEL tem largura; em tela cheia a edicao ocupa o que sobra */
          style={selectedId === null ? { width: resumoWidth } : undefined}
        >
          {selectedId === null && resumoId !== null ? (
            <div className="wi-resumo__head">
              <span className="wi-resumo__chave">{resumo.data?.item.code ?? '—'}</span>

              {/* editar também daqui: quem está lendo o resumo não deve ter de
                  voltar à lista só para achar o código */}
              <Link to={rotaDoItem(resumoId)} className="wi-resumo__editar">
                <i className="fas fa-pen" aria-hidden /> Abrir em página
              </Link>

              <button
                type="button"
                className={`wi-resumo__acao${resumoFixado ? ' wi-resumo__acao--on' : ''}`}
                onClick={() => setResumoFixado((v) => !v)}
                aria-pressed={resumoFixado}
                aria-label={resumoFixado ? 'Desafixar o resumo' : 'Fixar o resumo'}
                title={resumoFixado ? 'Desafixar: volta a recolher ao clicar fora' : 'Fixar: não recolhe ao clicar fora'}
              >
                <i className="fas fa-thumbtack" aria-hidden />
              </button>

              <button
                type="button"
                className="wi-resumo__acao"
                onClick={() => setResumoId(null)}
                aria-label="Fechar o resumo"
                title="Fechar o resumo"
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>
          ) : null}

      {selectedId === null && resumoId === null ? (
        <div className="wi-detail" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="wi-empty">
            <div className="wi-empty__icon">
              <i className="fas fa-tasks" aria-hidden />
            </div>
            <span className="wi-empty__title">Selecione um Item de Trabalho</span>
            <span className="wi-empty__text">Clique numa linha para ver o resumo, ou no código para editar.</span>
          </div>
        </div>
      ) : selectedId === null ? (
        /* RESUMO do item clicado na linha — a lista continua à esquerda */
        resumo.isPending || bootstrap.isPending ? (
          <div className="wi-detail">
            <LoadingState variant="detail" />
          </div>
        ) : resumo.isError || !resumo.data || !bootstrap.data ? (
          <div className="wi-detail">
            <Alert tone="danger" title="Não foi possível carregar o resumo">
              Tente de novo, ou abra o item pelo código.
            </Alert>
          </div>
        ) : (
          <ResumoDoItem
            detail={resumo.data}
            rotuloDe={(row) => catalogLabel(row)}
            statusOpcoes={(bootstrap.data?.workItemStatuses ?? []).filter((x) => x.isActive)}
            onTrocarStatus={(statusId) => trocarStatus.mutate({ itemId: resumoId!, statusId })}
            prioridadeOpcoes={(bootstrap.data?.priorities ?? []).filter((x) => x.isActive)}
            onTrocarPrioridade={(priorityId) => trocarPrioridade.mutate({ itemId: resumoId!, priorityId })}
            /* só para onde o fluxo deixa ir, a partir da etapa de agora */
            etapaOpcoes={proximasEtapas(
              nosDeFluxo.data?.nodes ?? [],
              nosDeFluxo.data?.transitions ?? [],
              resumo.data.item.flowStepId,
            ).map((no) => ({ id: no.flowStepId, rotulo: rotuloDaEtapa(no) }))}
            onTrocarEtapa={(flowStepId) => trocarEtapa.mutate({ itemId: resumoId!, flowStepId })}
            onVincularIniciativa={() => navigate(rotaDoItem(resumoId!))}
            /* troca ali mesmo, como status e prioridade — não leva à edição */
            responsavelOpcoes={(bootstrap.data?.members ?? []).filter((m) => m.isActive)}
            onTrocarResponsavel={(memberId) => trocarResponsavel.mutate({ itemId: resumoId!, memberId })}
            responsavel={
              (bootstrap.data?.members ?? []).find((m) => m.id === resumo.data.item.assigneeMemberId) ?? null
            }
            prioridade={catalogLabel(
              (bootstrap.data?.priorities ?? []).find((x) => x.id === resumo.data.item.workItemPriorityId),
            )}
            etapa={(() => {
              const no = (nosDeFluxo.data?.nodes ?? []).find(
                (n) => n.flowStepId === resumo.data.item.flowStepId,
              )
              return no ? rotuloDaEtapa(no) : ''
            })()}
            nomeDe={(id) => (bootstrap.data?.members ?? []).find((m) => m.id === id)?.name ?? 'Alguém'}
          />
        )
      ) : mostrarFilhos && itemPai ? (
        <WorkItemChildrenView
          pai={
            {
              ...itemPai,
              typeIcon: itemPai.type?.icon ?? null,
              typeTone: itemPai.type?.tone ?? null,
              typeLabel: rotulo(itemPai.type as never),
            } as never
          }
          // a árvore inteira: checklists de todos os descendentes (2026-08-30)
          todos={(cronograma.data?.items ?? []).map(
            (i: { tone?: string | null; isTerminal?: boolean | null; typeNameKey?: string | null }) => ({
              ...i,
              // o nome do tipo já resolvido, para o hint do ícone
              typeLabel: i.typeNameKey ? t(i.typeNameKey) : null,
              status: { tone: i.tone ?? 'neutral', isTerminal: Boolean(i.isTerminal) },
            }),
          ) as never}
          checklists={montarChecklists(cronograma.data) as never}
          onDatasDaLinha={(checklistItemId, datas) => datasDaLinha.mutate({ checklistItemId, ...datas })}
          onDatasDoItem={(itemId, datas) => datasDoItem.mutate({ itemId, ...datas })}
          onDatasDoChecklist={(checklistId, datas) => datasDoChecklist.mutate({ checklistId, ...datas })}
          statusOpcoes={(bootstrap.data?.workItemStatuses ?? [])
            .filter((s) => s.isActive)
            .map((s) => ({ id: s.id, label: rotulo(s), tone: s.tone, isTerminal: s.isTerminal }))}
          etapaOpcoes={(nosDeFluxo.data?.nodes ?? []).map((no) => ({
            id: no.flowStepId,
            label: `${no.flowName} · ${no.stepName}`,
          }))}
          onTrocarStatus={(itemId, statusId) => trocarStatus.mutate({ itemId, statusId })}
          onTrocarEtapa={(itemId, flowStepId) => trocarEtapa.mutate({ itemId, flowStepId })}
          onAbrir={(childId) => open(childId)}
          onEditarPai={() => {
            // fechar o detalhe do pai volta para a lista de filhos
            setTrail((c) => [...c, itemPai.id])
            navigate(rotaDoItem(itemPai.id))
          }}
          onFechar={goBack}
        />
      ) : detail.isPending || bootstrap.isPending ? (
        <div className="wi-detail">
          <LoadingState variant="detail" />
        </div>
      ) : detail.isError || !detail.data || !bootstrap.data ? (
        <div className="wi-detail">
          <Alert tone="danger" title="Não foi possível carregar">
            Verifique o link ou tente novamente.
          </Alert>
        </div>
      ) : (
        <WorkItemDetailView
          detail={detail.data}
          bootstrap={bootstrap.data}
          onOpenItem={(id) => open(id)}
          onBack={goBack}
          // o X fecha sempre: com trilha volta ao item anterior, sem trilha
          // volta para a lista sem item aberto
          canGoBack
        />
      )}

        </div>
      ) : null}

      {/*
        "Para quem é esta coluna?" (SPEC-20261103-1217) — só para quem pode pôr
        para todos, e só sem filtro: com filtro, as colunas são do filtro.
        Fechar sem escolher deixa como está: só para a pessoa.
      */}
      {paraQuem ? (
        <Modal
          open
          onClose={() => responderParaQuem(false)}
          title={
            paraQuem.acao === 'por'
              ? `Coluna “${rotuloDaColuna(paraQuem.k, catalogo)}”: para quem?`
              : `Tirar “${rotuloDaColuna(paraQuem.k, catalogo)}” de quem?`
          }
          width={460}
        >
          <div className="wi-paraquem__opcoes">
            <button type="button" className="wi-paraquem__opcao" autoFocus onClick={() => responderParaQuem(false)}>
              <i className="fas fa-user" aria-hidden />
              <span>
                Só para mim
                <small>
                  {paraQuem.acao === 'por'
                    ? 'A coluna aparece só na sua lista.'
                    : 'Some só da sua lista; os outros continuam vendo.'}
                </small>
              </span>
            </button>
            <button
              type="button"
              className="wi-paraquem__opcao"
              disabled={paraTodos.gravar.isPending}
              onClick={() => responderParaQuem(true)}
            >
              <i className="fas fa-users" aria-hidden />
              <span>
                Para todos
                <small>
                  {paraQuem.acao === 'por'
                    ? kanbanBoardId
                      ? 'Aparece na lista deste quadro de todo mundo, sem filtro.'
                      : 'Aparece na lista de Itens de todo mundo do workspace, sem filtro.'
                    : 'Some da lista de todo mundo que a recebeu.'}
                </small>
              </span>
            </button>
          </div>
        </Modal>
      ) : null}

      {/* Reabrir em cadeia pela trilha: a mesma tela da edição do item */}
      {reabrindoEmCadeia ? (
        <ReabrirEmCadeiaModal
          itemId={reabrindoEmCadeia.pedido.itemId}
          titulo={reabrindoEmCadeia.titulo}
          nos={reabrindoEmCadeia.nos}
          // só este: o pedido do checkbox, que pede o motivo da reabertura
          onSoEste={() => marcarTrilha.mutate(reabrindoEmCadeia.pedido)}
          onFechar={() => setReabrindoEmCadeia(null)}
        />
      ) : null}

      {/* Concluir em cadeia pela trilha: a mesma tela da edição do item */}
      {concluindoEmCadeia ? (
        <ConcluirEmCadeiaModal
          itemId={concluindoEmCadeia.itemId}
          titulo={concluindoEmCadeia.titulo}
          concluindo={marcarTrilha.isPending}
          onFechar={() => setConcluindoEmCadeia(null)}
          onConcluir={() => {
            const { titulo: _titulo, ...pedido } = concluindoEmCadeia
            marcarTrilha.mutate({ ...pedido, cascata: true })
          }}
        />
      ) : null}

      {/* Motivo da reabertura pedido por um checkbox da trilha (SPEC-20261007-2155-trilha-de-etapas-e-status) */}
      <MotivoDaReabertura
        aberto={pedindoMotivo !== null}
        codigo={pedindoMotivo?.codigo ?? ''}
        statusDestino={pedindoMotivo?.statusDestino ?? null}
        salvando={marcarTrilha.isPending}
        onFechar={() => setPedindoMotivo(null)}
        onConfirmar={(motivo) => {
          if (!pedindoMotivo) return
          const { statusDestino: _destino, ...pedido } = pedindoMotivo
          marcarTrilha.mutate({ ...pedido, comment: motivo })
        }}
      />

      {/* Template ou em branco, na criação (SPEC-20260830-1225) */}
      {escolherTemplate ? (
        <EscolherTemplateModal
          {...(kanbanBoardId ? { kanbanBoardId } : {})}
          onFechar={() => setEscolherTemplate(false)}
          onEmBranco={() => {
            setEscolherTemplate(false)
            void criarEAbrir()
          }}
          onAplicado={(rootId) => {
            setEscolherTemplate(false)
            setTrail([])
            // já nasce completo: abre no modo normal, sem `?novo=1`
            navigate(rotaDoItem(rootId))
          }}
        />
      ) : null}
    </div>
  )
}
